import React, { useState, useRef, useLayoutEffect, useEffect, useCallback, useMemo } from "react";
import { motion, LayoutGroup } from "framer-motion";
import {
  Target, Compass as CompassIcon, Plus, Pencil, Scissors, Merge, Link2, CornerDownRight,
  ArrowUpRight, HelpCircle, Trash2, Check, X, Sparkles, GitBranch, Undo2,
  MessageSquareText, ChevronUp, ChevronDown, LayoutList, IndentIncrease, MoreHorizontal, Tag, PenLine,
} from "lucide-react";
import { ComposeUnit } from "@/components/ComposeUnit";
import { toast } from "sonner";
import { otNoPaste, otOwnCopy } from "@/lib/otPasteGuard";
import { clearDraft, draftChanged, getDraft, setDraft } from "@/lib/writingDraft";
import { autoGrow, EDIT_MIN_PX } from "@/lib/autoGrow";
import { placedNodeIdSet } from "@/lib/otMapEdges";
import { unitView, unitStatus, isProvisional, unitEditText, segmentUnitText, paragraphLike } from "@/lib/unitText";

// TWO DIFFERENT ACTS, NAMED BY WHAT THE STUDENT DOES: EDIT TEXT = change the wording yourself, in place (one save, no coach turn);
// WORK WITH COACH = open this part's own coached writing state (its local dialogue + Paragraph Function Map) to develop or improve it.
export const EDIT_TEXT_TIP = "Change the wording yourself.";
export const WORK_WITH_COACH_TIP = "Get help developing or improving this part.";

const TERRA = "#8C3A2A";
const SHOW_CROSS_EDGES = false; // correction #2 — cross-map relationship arrows/chips hidden (data preserved)

// SIMPLIFY (UDWW 3C-5): the PART_ROLE / FUNCTION annotations ("major part", "claim", "reason"…) are no longer rendered
// on cards — TWS keeps `structural_role` / `tws_function` privately (data untouched); the student sees writing + headings.

// CLEANUP (2026-06): the GENERIC student-facing "+ Question / + Subquestion" controls are HIDDEN.
// An unexplained generic "make a Question object" button does not need to be visible; questions
// REMAIN a first-class developmental/structural object — TWS introduces and captures them
// contextually from the dialogue (question_capture) and drives question decomposition via the
// `differentiate` operation. ALL underlying machinery is retained and dormant (onCreateQuestion,
// the subq/question-under add flows, question rendering/nesting/placement/transfer). Flip to true
// to restore the manual controls.
const SHOW_GENERIC_ADD_QUESTION = false;

// OT v1.3.4 — THE THINKING MAP with direct manipulation.
// One visible structure: THESIS (integrating node) → QUESTION strands → ANSWERS (idea whose parent
// is a question) → SUBIDEAS (idea whose parent is an idea). No new node types: role is INFERRED from
// parent type, so nodes stay freely reparentable. Drag-and-drop, click "Move under…/Pull out", and
// (fast-follow) verbal TWS all invoke the SAME structural operations: onStructuralMove / onPullOut.
export function MindMap({
  thesis, thesisStatus, thesisRecognized = false, questions, items, rels, qrels,
  focusIds = [], focusQIds = [], coachBusy, spacious = false,
  regionsActive = false, thesisRegion = "", onPlaceThesisRegion,
  frame = "essay",   // CONSOLIDATED LOOP: "writing" = the emerging thesis + the student's writing only; "parts" = the Structure view of the essay (Introduction / Body / Conclusion)
  teaching = null,   // STRUCTURED CONTENT projection (ot.consolidated.teaching) — kept as data for the progress block beside the conversation; no longer rendered on cards
  // STRUCTURE VIEW AS ORDINARY WRITING (frame "parts"): split a paragraph at the cursor / join it with the next — the same canonical
  // transformations (separate / join routes), no coach turn merely for the act; `refineOpen` = the student chose to move toward paragraph
  // refinement, so every part offers "Work on this part" and THEY pick which
  onSplitParagraph, onJoinNext, refineOpen = false,
  latentStructure = null, descent = null, onAdoptProvisional, onRejectProvisional,
  // UDWW 3C-1 — TWS-provisional STRUCTURE the student decides in place: a functional read (dismiss), a relation (keep / not this)
  onDismissRole, onAdoptProvisionalRel, onRejectProvisionalRel,
  onSaveThesis, onWorkOn, onDevelop, onSaveIdea, onSaveQuestion,
  onStructuralMove, onPullOut, onDelete, onConnect, onDeleteRel, onSeparate, onJoin, onToggleUncertain, onMakeSection,
  onCreateQuestion, onCreateIdea, onAddLoose, onUndo, canUndo, onReorderSiblings, developSignal,
  // UDWW Pass 3B — compose locally: registry (ot.compose), readiness view (ot.compose_ready), the focused unit + its view
  compose = {}, composeReady = {}, composeFocus = null, composeView = null, composeBusy = false,
  onComposeOpen, onComposeReturn, onComposeTurn,
  // UDWW 3C-4b — a WORKING TITLE given to a paragraph-like unit is a developmental act TWS reads (title ⇄ paragraph)
  onTitleGiven,
  // SIMPLIFY (UDWW 3C-5) — the EMERGING THESIS candidate (student's words) TWS recognised, shown in the structure while it is decided
  thesisProposal = null,
  // ROUTING — TWS's persistent provisional reading of the emerging thesis (ot.provisional_thesis; the student's own words)
  provisionalThesis = null,
  draftScope = "",   // WRITING ENTRY: unsaved text is kept (lib/writingDraft) per session across record refreshes and view switches
}) {
  const [selected, setSelected] = useState(null);           // {type:'idea'|'question', id}
  const [mode, setMode] = useState(null);                    // {kind:'move'|'connect', srcType, srcId}
  const [moveLevel, setMoveLevel] = useState("top");         // progressive disclosure: 'top' | 'ideas' (other cards)
  const [editing, setEditing] = useState(null);              // {type, id, text}
  const [titleEdit, setTitleEdit] = useState(null);          // inline student card-title editor: idea id
  const [titleVal, setTitleVal] = useState("");              // draft short title (student's words; metadata only)
  const [sep, setSep] = useState(null);                      // {id, parts:[]}
  const [funcPick, setFuncPick] = useState(null);            // REPAIR §17: section id whose compositional function is being named
  const [moreFor, setMoreFor] = useState(null);              // usability pass: node id whose secondary actions are expanded
  const [relView, setRelView] = useState(null);              // REPAIR §7: tap-to-reveal full relationship {id, text}
  const [connectLabel, setConnectLabel] = useState(null);    // {fromId, toId, text}
  const [confirmDel, setConfirmDel] = useState(null);        // {type, id}
  const [adding, setAdding] = useState(null);                // {kind:'subq'|'answer'|'beneath', parentType, parentId, text}
  const [regionWrite, setRegionWrite] = useState(null);      // {key, text}: a paragraph written straight into a broad region (the target for pasting their own words)
  const [expanded, setExpanded] = useState({});
  const [histOpen, setHistOpen] = useState({});            // UNIFIED WORKSPACE: a unit's earlier wording, on demand
  const [thesisEdit, setThesisEdit] = useState(false);
  const [thesisDraft, setThesisDraft] = useState(thesis || "");
  const [addOpen, setAddOpen] = useState(false);
  const [addKind, setAddKind] = useState("idea");
  const [addText, setAddText] = useState("");
  const [tick, setTick] = useState(0);
  // drag-and-drop state (native HTML5)
  const [drag, setDrag] = useState(null);                    // {type, id}
  const dragRef = useRef(null);                              // synchronous drag source — completing a drop must
                                                             // not depend on React state timing / re-render
  const dragging = !!drag;                                   // while true, framer-motion layout animation is
                                                             // disabled so the map does not reflow mid-drag
                                                             // (mid-drag transforms break native HTML5 drop)
  const [dropTarget, setDropTarget] = useState(null);        // {type:'question'|'idea'|'root'|'loose', id?}
  const [dropLine, setDropLine] = useState(null);            // {target, beforeId}
  // ESSAY-AS-WORKSPACE (frame "parts" — the consolidated loop's Structure view): the student's writing seen structurally, edited with familiar
  // writing actions; the old map's node operations stay in the data path but are not permanently student-facing here
  const partsFrame = frame === "parts";
  const [splitHint, setSplitHint] = useState(false);         // the editor was opened from "Split": say where to click, then Split here
  const editRef = useRef(null);                              // the open editor's textarea (the cursor position is the split point)

  // ThinkingWorkspace pings developSignal (a counter) to open the add-idea input from the orientation CTA.
  useEffect(() => {
    if (developSignal) { setAddOpen(true); setAddKind("idea"); }
  }, [developSignal]);
  // ESCAPE leaves Move mode / closes More (nothing changes) — only while no editor owns the key
  useEffect(() => {
    if (editing || !((mode && mode.kind === "move") || moreFor)) return undefined;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      if (moreFor) setMoreFor(null);
      if (mode && mode.kind === "move") { setMode(null); setMoveLevel("top"); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mode, editing, moreFor]);
  // More stays local to the card it was opened on: selecting another card (or none) closes it
  useEffect(() => { if (moreFor && (!selected || selected.id !== moreFor)) setMoreFor(null); }, [selected, moreFor]);

  const studentQs = questions.filter((q) => q.source === "student");
  const qById = (id) => questions.find((q) => q.id === id);
  const itemById = (id) => items.find((i) => i.id === id);
  // click-move helpers: which card is being moved (so the banner names it and the source shows as "moving")
  const inMove = !!(mode && mode.kind === "move");
  // ADF FOCUS (Relational-agency repair §8/§16): when TWS foregrounds a SET of cards for a relational act,
  // gently dim the rest so the active objects are unmistakable. "Dimmed" means "not active right now", not
  // "unimportant" — the whole map stays visible & interactive, and salience restores when the focus clears.
  const dimForFocus = !inMove && Array.isArray(focusIds) && focusIds.length >= 2;
  const _movingObj = inMove ? (mode.srcType === "idea" ? itemById(mode.srcId) : qById(mode.srcId)) : null;
  const movingText = (_movingObj && _movingObj.text) || "";
  const shortOf = (i) => (i?.label || "").trim() || i?.text || "";

  // ---- hierarchy helpers ----
  const childQIds = (qid) => qrels.filter((r) => r.to_id === qid).map((r) => r.from_id).filter((id) => qById(id));
  const isChildQ = (qid) => qrels.some((r) => r.from_id === qid && qById(r.to_id));
  const rootQs = studentQs.filter((q) => !isChildQ(q.id) && !q.parent_item_id && !q.unplaced);
  const childQuestionsOfIdea = (ideaId) => studentQs.filter((q) => q.parent_item_id === ideaId);
  const unplacedQs = studentQs.filter((q) => q.unplaced && !isChildQ(q.id) && !q.parent_item_id);
  const primaryQ = (i) => (i.question_ids || []).find((qid) => qById(qid));
  const directIdeasFor = (qid) => items.filter((i) => !i.parent_id && primaryQ(i) === qid);
  const childIdeas = (id) => items.filter((i) => i.parent_id === id);
  const looseIdeas = items.filter((i) => !i.parent_id && !primaryQ(i));

  const ideaDescendants = (id, acc = new Set()) => {
    childIdeas(id).forEach((c) => { if (!acc.has(c.id)) { acc.add(c.id); ideaDescendants(c.id, acc); } });
    return acc;
  };
  const questionDescendants = (qid, acc = new Set()) => {
    childQIds(qid).forEach((c) => { if (!acc.has(c)) { acc.add(c); questionDescendants(c, acc); } });
    return acc;
  };
  const collectIdeaTree = (id, acc) => { acc.add(id); childIdeas(id).forEach((c) => collectIdeaTree(c.id, acc)); childQuestionsOfIdea(id).forEach((q) => questionSubtreeIdeas(q.id, acc)); };
  const questionSubtreeIdeas = (qid, acc = new Set()) => {
    directIdeasFor(qid).forEach((i) => collectIdeaTree(i.id, acc));
    childQIds(qid).forEach((sq) => questionSubtreeIdeas(sq, acc));
    return acc;
  };

  // ---- UNIFIED SIBLING ORDER (v1.3.5) ----
  // Parent relationship and sibling order are INDEPENDENT. Children of one parent (questions AND ideas
  // together) render as ONE ordered list, sorted by a persistent `position`; nodes without a position
  // fall back to creation time. There is NO "questions above ideas" grouping.
  const cmpSib = (a, b) => {
    const pa = typeof a.position === "number" ? a.position : null;
    const pb = typeof b.position === "number" ? b.position : null;
    if (pa !== null && pb !== null) return pa - pb;
    if (pa !== null) return -1;
    if (pb !== null) return 1;
    return String(a.created_at || "").localeCompare(String(b.created_at || ""));
  };
  const mergedQChildren = (qid) => {
    const subs = childQIds(qid).map(qById).filter(Boolean).map((n) => ({ kind: "q", node: n }));
    const ideas = directIdeasFor(qid).map((n) => ({ kind: "i", node: n }));
    return [...subs, ...ideas].sort((a, b) => cmpSib(a.node, b.node));
  };
  const mergedIdeaChildren = (id) => {
    const kids = childIdeas(id).map((n) => ({ kind: "i", node: n }));
    const cqs = childQuestionsOfIdea(id).map((n) => ({ kind: "q", node: n }));
    return [...kids, ...cqs].sort((a, b) => cmpSib(a.node, b.node));
  };

  // ---- FREE SIBLING REORDER (Move Up / Move Down — deterministic, no drag needed) ----
  // Resolve the node's CURRENT parent and its merged (interleaved) sibling list, then swap with the
  // neighbour and persist the whole order. Parent is never changed; whole subtrees ride along.
  const parentGroupOf = (node) => {
    if (node.type === "question") {
      const rel = qrels.find((r) => r.from_id === node.id && qById(r.to_id));
      if (rel) return mergedQChildren(rel.to_id);
      const q = qById(node.id);
      if (q && q.parent_item_id) return mergedIdeaChildren(q.parent_item_id);
      return mergedRoots();
    }
    const it = itemById(node.id);
    if (it && it.parent_id) return mergedIdeaChildren(it.parent_id);
    const pq = it && primaryQ(it);
    if (pq) return mergedQChildren(pq);
    return mergedRoots();
  };
  const siblingIndex = (node) => parentGroupOf(node).findIndex((s) => s.node.id === node.id);
  const canUp = (node) => siblingIndex(node) > 0;
  const canDown = (node) => { const s = parentGroupOf(node); const i = s.findIndex((x) => x.node.id === node.id); return i >= 0 && i < s.length - 1; };
  const moveSibling = async (node, dir) => {
    const sibs = parentGroupOf(node);
    const idx = sibs.findIndex((s) => s.node.id === node.id);
    const j = dir === "up" ? idx - 1 : idx + 1;
    if (idx < 0 || j < 0 || j >= sibs.length) return;
    const reordered = [...sibs];
    [reordered[idx], reordered[j]] = [reordered[j], reordered[idx]];
    const order = reordered.map((s) => ({ type: s.kind === "q" ? "question" : "idea", id: s.node.id }));
    await onReorderSiblings(order);
    bump();
  };

  // ---- cross-branch relationship connectors (SVG overlay) ----
  const mapRef = useRef(null);
  const nodeRefs = useRef({});
  const [edges, setEdges] = useState([]);
  const [dims, setDims] = useState({ w: 0, h: 0 });
  const registerNode = (id) => (el) => { if (el) nodeRefs.current[id] = el; else delete nodeRefs.current[id]; };

  // FAIL-SAFE: only cards that are legitimately PLACED in the structure may anchor a structural edge. An
  // unplaced tray card keeps a DOM ref, but its relationships must NOT be drawn (no floating edges to/from
  // "NOT PLACED YET"). Pure mirror of the map's placement rules — see lib/otMapEdges.
  const placedNodeIds = useMemo(() => placedNodeIdSet(items, questions), [items, questions]);

  const recompute = useCallback(() => {
    const cont = mapRef.current;
    if (!cont) return;
    const cr = cont.getBoundingClientRect();
    const out = [];
    rels.forEach((r) => {
      if (!placedNodeIds.has(r.from_id) || !placedNodeIds.has(r.to_id)) return;  // guard: both endpoints placed
      const a = nodeRefs.current[r.from_id];
      const b = nodeRefs.current[r.to_id];
      if (!a || !b) return;
      const ar = a.getBoundingClientRect();
      const br = b.getBoundingClientRect();
      out.push({
        id: r.id,
        tws: r.provenance === "tws_provisional" || r.provenance === "tws_inferred",   // PROPOSAL vs OWNERSHIP: TWS's reading until the student keeps / makes it
        x1: ar.left + ar.width / 2 - cr.left + cont.scrollLeft,
        y1: ar.top + ar.height / 2 - cr.top + cont.scrollTop,
        x2: br.left + br.width / 2 - cr.left + cont.scrollLeft,
        y2: br.top + br.height / 2 - cr.top + cont.scrollTop,
        label: r.relation_text || "",
      });
    });
    setEdges(out);
    setDims({ w: cont.scrollWidth, h: cont.scrollHeight });
  }, [rels, placedNodeIds]);

  useLayoutEffect(() => { recompute(); }, [recompute, items, rels, qrels, expanded, tick, thesis]);
  useEffect(() => { const t = setTimeout(recompute, 360); return () => clearTimeout(t); }, [recompute, items, rels, qrels]);
  useEffect(() => {
    const cont = mapRef.current;
    const ro = new ResizeObserver(recompute);
    if (cont) ro.observe(cont);
    window.addEventListener("resize", recompute);
    return () => { ro.disconnect(); window.removeEventListener("resize", recompute); };
  }, [recompute]);
  useEffect(() => { setThesisDraft(thesis || ""); }, [thesis]);

  const bump = () => setTick((t) => t + 1);
  // REPAIR §7: compact relationship chip — the CHIP represents the relation; the student's full sentence is
  // preserved and revealed on tap (works on touch). Never truncate the stored relation, only the display.
  const compactRel = (t) => { const s = (t || "").trim(); return s.length <= 24 ? s : `${s.slice(0, 22).trimEnd()}…`; };

  // LIVING WORKSPACE (§16-19): auto-number BODY sections in reader order; renumber automatically on reorder.
  // Introduction/Conclusion show their identity, not a number. The content label stays visually primary.
  const sectionNumber = useMemo(() => {
    const map = {}; let n = 0;
    (items || []).filter((x) => !x.parent_id).forEach((x) => {
      if (x.node_kind === "section" && ((x.composition_function || "body") === "body")) { n += 1; map[x.id] = n; }
    });
    return map;
  }, [items]);
  const isSectionNode = (i) => i && (i.node_kind === "section" || i.node_kind === "subsection");
  const sectionIdentity = (i) => {
    const cf = (i.composition_function || (i.node_kind === "section" ? "body" : "")).toLowerCase();
    if (i.node_kind === "subsection") return "Subsection";
    if (cf === "introduction") return "Introduction";
    if (cf === "conclusion") return "Conclusion";
    return sectionNumber[i.id] ? `Section ${sectionNumber[i.id]}` : "Section";
  };
  const functionText = (cf) => ({
    introduction: "orients the reader and sets up the piece",
    body: "develops part of your answer",
    conclusion: "pulls it together for the reader",
    other: "",
  }[(cf || "").toLowerCase()] || "");
  const toggleExpand = (id) => setExpanded((s) => ({ ...s, [id]: s[id] === false ? true : false }));
  // A CLICK ELSEWHERE COMMITS an edited card through the same save (never a silent discard); an unchanged editor simply closes.
  const clearInteractions = () => {
    setSelected(null); setMode(null); setSep(null); setAdding(null); setConfirmDel(null);
    if (regionWrite && regionWrite.text.trim()) saveRegionWrite();
    else if (regionWrite) setRegionWrite(null);
    if (editing && editing.type === "idea" && draftChanged(getDraft(draftScope, editing.id))) { saveEditing(); return; }
    if (editing) clearDraft(draftScope, editing.id);
    setEditing(null);
  };

  // ---- unified drop validity (shared by drag AND click-move) ----
  const canDrop = (node, target) => {
    if (!node || !target) return false;
    if (target.type === "region") return node.type === "idea" || node.type === "question"; // FO-unification: place a part in an essay region
    if (target.type === "root" || target.type === "loose") return true;
    if (target.type === "question") {
      if (node.type === "question") return node.id !== target.id && !questionDescendants(node.id).has(target.id);
      return true; // idea → answer under a question
    }
    if (target.type === "idea") {
      if (node.type === "idea") return node.id !== target.id && !ideaDescendants(node.id).has(target.id);
      return !questionSubtreeIdeas(node.id).has(target.id); // question → under an idea (block cycles)
    }
    return false;
  };

  // ---- drag-and-drop ----
  const clearDrag = () => { dragRef.current = null; setDrag(null); setDropTarget(null); setDropLine(null); };
  const doMove = async (node, target, beforeId) => {
    const n = node || dragRef.current; clearDrag();
    if (!n || !canDrop(n, target)) return;
    await onStructuralMove(n, target, beforeId || null);
    bump();
  };
  const nodeDragProps = (type, id) => ({
    draggable: !partsFrame,   // Structure view: a card is the student's paragraph — mouse selection of its words (copy / cut) must work; placement goes through Move
    onDragStart: (e) => { e.stopPropagation(); dragRef.current = { type, id }; setSelected(null); setMode(null); setDrag({ type, id }); try { e.dataTransfer.setData("text/plain", id); e.dataTransfer.effectAllowed = "move"; } catch (_) {} },
    onDragEnd: () => clearDrag(),
    onDragOver: (e) => { const d = dragRef.current || drag; if (d && canDrop(d, { type, id })) { e.preventDefault(); e.stopPropagation(); setDropTarget({ type, id }); setDropLine(null); } },
    onDrop: (e) => { const d = dragRef.current || drag; if (d && canDrop(d, { type, id })) { e.preventDefault(); e.stopPropagation(); doMove(d, { type, id }, null); } },
  });

  // a thin insertion zone for sibling REORDER (visible while dragging ANY compatible node — questions
  // and ideas share one order, so a card may drop before/after a sibling of either type)
  const DropLine = ({ target, beforeId }) => {
    if (!drag || !canDrop(drag, target)) return null;
    const active = dropLine && dropLine.beforeId === (beforeId || "END") &&
      dropLine.target.type === target.type && dropLine.target.id === target.id;
    return (
      <div data-testid="mm-dropline" data-parent={target.id || "root"} data-before={beforeId || "END"}
        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDropTarget(null); setDropLine({ target, beforeId: beforeId || "END" }); }}
        onDragLeave={(e) => { e.stopPropagation(); }}
        onDrop={(e) => { e.preventDefault(); e.stopPropagation(); doMove(dragRef.current || drag, target, beforeId || null); }}
        className={`transition-all rounded-md flex items-center justify-center overflow-hidden ${active
          ? "my-2.5 h-12 border-2 border-dashed border-emerald-500 bg-emerald-100 ring-2 ring-emerald-300"
          : "my-1.5 h-6 border-2 border-dashed border-emerald-400/60 bg-emerald-50/60 hover:bg-emerald-100/80 hover:border-emerald-500"}`}>
        <span className={`font-medium text-emerald-700 transition-all ${active ? "text-[12px] opacity-100" : "text-[10px] opacity-70"}`}>
          {active ? "↓ Drop here ↓" : "drop to place here"}
        </span>
      </div>
    );
  };

  // ---- click-based move / connect ----
  const startMove = (type, id) => { setMode({ kind: "move", srcType: type, srcId: id }); setMoveLevel("top"); setSelected(null); };
  const startConnect = (id) => { setMode({ kind: "connect", srcType: "idea", srcId: id }); setSelected(null); };
  const canBeConnectTarget = (targetId) => mode && mode.kind === "connect" && targetId !== mode.srcId;
  // LIVING UNIT BOUNDARIES (UDWW pass 2): JOIN = "these are better understood as one developing unit" — the unit
  // the student started from keeps its identity; the other's words follow, verbatim; both stay recoverable as history.
  const startJoin = (id) => { setMode({ kind: "join", srcType: "idea", srcId: id }); setSelected(null); };
  const canBeJoinTarget = (targetId) => mode && mode.kind === "join" && targetId !== mode.srcId
    && !isProvisional(itemById(targetId) || {}) && (itemById(targetId) || {}).node_kind !== "group";

  const chooseTarget = async (target) => {
    if (!mode) return;
    if (mode.kind === "move") {
      const src = { type: mode.srcType, id: mode.srcId };
      if (!canDrop(src, target)) return;
      await onStructuralMove(src, target, null);
      setMode(null); bump();
    } else if (mode.kind === "connect" && target.type === "idea" && canBeConnectTarget(target.id)) {
      setConnectLabel({ fromId: mode.srcId, toId: target.id, text: "" });
      setMode(null);
    } else if (mode.kind === "join" && target.type === "idea" && canBeJoinTarget(target.id)) {
      const src = mode.srcId;
      setMode(null);
      await onJoin(src, target.id);
      bump();
    }
  };
  const nodeClick = (type, id) => {
    if (mode) { chooseTarget({ type, id }); return; }
    setSelected((s) => (s && s.type === type && s.id === id ? null : { type, id }));
  };

  const commitConnect = async () => { if (!connectLabel) return; await onConnect(connectLabel.fromId, connectLabel.toId, connectLabel.text.trim()); setConnectLabel(null); bump(); };
  // ONE SAVE: Return, the Save button, a click elsewhere and the commit before a TWS action all end here — the same call the record has.
  const saveEditing = async () => {
    if (!editing) return;
    clearTimeout(autosaveTimer.current);
    const ed = editing;
    const txt = ed.text.trim();
    clearDraft(draftScope, ed.id);                                       // cleared first: nothing commits twice
    try {
      // AUTHENTIC REPAIR 1: saving the visible text unchanged is a no-op unless developments were shown (then it folds them into the one text)
      if (txt && (txt !== (ed.initial || "").trim() || ed.folds)) {
        if (ed.type === "idea") await onSaveIdea(ed.id, txt, partsFrame ? undefined : (ed.label || "").trim());   // Structure view: the words only (no label field)
        else await onSaveQuestion(ed.id, txt);
      }
      setEditing(null); setSplitHint(false); bump();
    } catch {
      setDraft(draftScope, ed.id, ed.text, ed.initial);                  // the save failed: the words stay in the editor (the open editor is the signal)
    }
  };
  // SAFE AUTOSAVE (Structure view): every keystroke is already in the recoverable draft store; a meaningful change also auto-persists through
  // the SAME onSaveIdea patch after a short pause / on leaving the editor — quietly, never a coaching turn. The editor stays open.
  const editingRef = useRef(null); editingRef.current = editing;
  const autosaveTimer = useRef(null);
  const autosaveEditing = async () => {
    const ed = editingRef.current;
    if (!ed || ed.type !== "idea") return;
    const txt = ed.text.trim();
    if (!txt || txt === (ed.initial || "").trim()) return;
    setEditing((x) => (x && x.id === ed.id ? { ...x, saveState: "saving" } : x));
    try {
      await onSaveIdea(ed.id, txt, partsFrame ? undefined : (ed.label || "").trim());
      setEditing((x) => (x && x.id === ed.id ? { ...x, autosaved: true, saveState: "saved" } : x));
      setTimeout(() => setEditing((x) => (x && x.id === ed.id && x.saveState === "saved" ? { ...x, saveState: "" } : x)), 1800);
    } catch { setEditing((x) => (x && x.id === ed.id ? { ...x, saveState: "" } : x)); }
  };
  const scheduleAutosave = () => { clearTimeout(autosaveTimer.current); autosaveTimer.current = setTimeout(autosaveEditing, 1200); };
  useEffect(() => () => clearTimeout(autosaveTimer.current), []);
  // CANCEL = discard THIS editing episode: if autosave already persisted, the editor-open text is restored through the same save
  const cancelEditing = async () => {
    if (!editing) return;
    clearTimeout(autosaveTimer.current);
    const ed = editing;
    clearDraft(draftScope, ed.id); setEditing(null); setSplitHint(false);
    if (ed.autosaved && ed.type === "idea" && (ed.initial || "").trim()) { try { await onSaveIdea(ed.id, ed.initial.trim(), partsFrame ? undefined : (ed.label || "").trim()); bump(); } catch { /* the record shows what stands */ } }
  };
  const changeEditing = (text) => { setDraft(draftScope, editing.id, text, editing.initial); setEditing({ ...editing, text, saveState: "editing" }); if (editing.type === "idea") scheduleAutosave(); };
  // VISIBLE TEXT == EDITABLE TEXT: the edit box opens on exactly what the unit shows (its text + what the conversation added).
  // Opening another card's editor COMMITS a changed one first, through the same save (the canvas-click rule) — words are never left behind.
  const startEdit = async (i, hint = false) => {
    if (editing && editing.type === "idea" && editing.id !== i.id && draftChanged(getDraft(draftScope, editing.id))) await saveEditing();
    const v = unitEditText(i); setDraft(draftScope, i.id, v, v);
    setSplitHint(!!hint);
    setEditing({ type: "idea", id: i.id, text: v, initial: v, folds: unitView(i).additions.length > 0, label: i.label || "" });
  };
  // SPLIT HERE — one paragraph becomes two at the cursor: the words as they stand in the editor, first part / second part, exact; the second
  // paragraph follows the first in the same region (the canonical separate route, client-computed halves). No coach turn for the act itself.
  const splitHere = async () => {
    if (!editing || editing.type !== "idea" || !onSplitParagraph) return;
    const el = editRef.current;
    const at = el && typeof el.selectionStart === "number" ? el.selectionStart : -1;
    const full = editing.text;
    const first = full.slice(0, Math.max(0, at)).trim(), second = full.slice(Math.max(0, at)).trim();
    if (at <= 0 || !first || !second) { toast("Click inside the paragraph where the new one should begin, then Split here."); return; }
    const ed = editing;
    clearDraft(draftScope, ed.id);
    try { await onSplitParagraph(ed.id, first, second, full.trim()); setEditing(null); setSplitHint(false); bump(); }
    catch { setDraft(draftScope, ed.id, ed.text, ed.initial); }
  };
  // JOIN WITH NEXT — this paragraph and the one after it (document order: Introduction → Body → Conclusion) become one, words unchanged
  const nextParagraphOf = (id) => {
    const g = regionBands();
    const order = [...g.introduction, ...g.body, ...g.conclusion].filter((c) => c.kind === "i").map((c) => c.node);
    const k = order.findIndex((n) => n.id === id);
    return k >= 0 && k + 1 < order.length ? order[k + 1] : null;
  };
  // RETURNING TO THE VIEW: a draft left behind on a card (view switch, reload) reopens there.
  useEffect(() => {
    if (editing) return;
    for (const i of items) {
      const d = getDraft(draftScope, i.id);
      if (draftChanged(d)) { setEditing({ type: "idea", id: i.id, text: d.text, initial: unitEditText(i), folds: unitView(i).additions.length > 0, label: i.label || "" }); return; }
    }
  }, [items]);   // eslint-disable-line react-hooks/exhaustive-deps
  // WRITING ENTRY: Return is an ordinary writing break (a writing editor, not a chat box); Cmd/Ctrl+Return saves now; Escape cancels.
  const entryKeys = (commit, cancel) => (e) => {
    if (e.key === "Escape") { e.preventDefault(); cancel(); }
    else if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); commit(); }
  };
  const commitSep = async () => { if (!sep) return; const parts = sep.parts.map((p) => p.trim()).filter(Boolean); if (parts.length < 2) return; await onSeparate(sep.id, parts); setSep(null); bump(); };
  // STUDENT-OWNED WORKING TITLE — the student's representation of the paragraph as a whole (NEVER alters the verbatim
  // text). Saved as label with label_authored so the backend keeps their words verbatim; TWS never authors it. When the
  // unit is paragraph-like the title is a developmental act: TWS is told to read TITLE ⇄ PARAGRAPH (3C-4b).
  const saveTitle = async (i) => {
    const t = titleVal.trim();
    const changed = t && t !== (i.label || "").trim();
    await onSaveIdea(i.id, i.text, t); setTitleEdit(null); bump();
    if (changed && onTitleGiven && isParagraphLike(i)) onTitleGiven(i, t);
  };
  // paragraph-like: enough words to do a job of its own, ideas gathered under it, or judged paragraph-ready
  const isParagraphLike = (i) => !isProvisional(i) && paragraphLike(i, { children: childIdeas(i.id).length, ready: !!(composeReady && composeReady[i.id] && composeReady[i.id].ready) });
  // KEEP AS TITLE — TWS's functional read becomes the student's working title (the SAME label PATCH; the backend records
  // `adopted_from_tws` and clears the read). Adoption is the student's act, so it is read as a title given.
  const keepRoleAsTitle = async (i) => {
    const role = (i.provisional_role || "").trim();
    if (!role) return;
    await onSaveIdea(i.id, i.text, role); bump();
    if (onTitleGiven && isParagraphLike(i)) onTitleGiven(i, role);
  };
  // renaming a TWS-inferred PART makes it the student's own (backend marks it student_adopted) — a natural,
  // no-ceremony way to adopt the shape. The part carries no substantive text, only its organizing label.
  const savePartTitle = async (part) => { await onSaveIdea(part.id, "", titleVal.trim()); setTitleEdit(null); bump(); };
  const commitAdd = async () => {
    if (!adding || !adding.text.trim()) return;
    const t = adding.text.trim();
    if (adding.kind === "subq") await onCreateQuestion({ type: "question", id: adding.parentId }, t);
    else if (adding.kind === "question-under") await onCreateQuestion({ type: "idea", id: adding.parentId }, t);
    else if (adding.kind === "answer") await onCreateIdea({ type: "question", id: adding.parentId }, t);
    else if (adding.kind === "beneath") await onCreateIdea({ type: "idea", id: adding.parentId }, t);
    setAdding(null); bump();
  };
  const requestDelete = (type, id) => {
    const hasKids = type === "idea"
      ? childIdeas(id).length > 0
      : (childQIds(id).length > 0 || directIdeasFor(id).length > 0);
    if (hasKids) setConfirmDel({ type, id });
    else { onDelete({ type, id }, "promote"); setSelected(null); }
  };

  // ---- small building blocks ----
  // a conceptual family inside More: a quiet heading + its eligible actions; renders nothing when no action is eligible
  const ActGroup = ({ testid, title, children }) => {
    const kids = React.Children.toArray(children).filter(Boolean);
    if (!kids.length) return null;
    return (
      <div data-testid={testid} role="group" aria-label={title} className="flex flex-wrap items-baseline gap-x-0.5 gap-y-0.5">
        <span className="w-full sm:w-auto sm:mr-1 font-mono text-[9px] uppercase tracking-[0.16em] text-stone-400 select-none">{title}</span>
        {kids}
      </div>
    );
  };

  const ActBtn = ({ testid, icon: Icon, label, onClick, danger, title }) => (
    <button data-testid={testid} title={title} onClick={(e) => { e.stopPropagation(); onClick(); }}
      className={`inline-flex items-center gap-1 text-[11px] px-1.5 py-1 rounded transition-colors ${danger ? "text-stone-400 hover:text-rose-500 hover:bg-rose-50" : "text-stone-600 hover:text-[#8C3A2A] hover:bg-[#8C3A2A]/5"}`}>
      <Icon className="h-3.5 w-3.5" />{label}
    </button>
  );

  // UDWW Pass 3B — a unit inside a branch that is being WRITTEN (an ancestor composing) is written there, not separately
  const ancestorComposing = (i) => {
    let p = itemById(i.parent_id); const seen = new Set();
    while (p && !seen.has(p.id)) { if (compose && compose[p.id] && compose[p.id].status === "composing") return true; seen.add(p.id); p = itemById(p.parent_id); }
    return false;
  };

  const ideaActions = (i) => {
    const isSec = isSectionNode(i);
    const showMore = moreFor === i.id;
    const cEntry = compose && compose[i.id];
    const cReady = !!(composeReady && composeReady[i.id] && composeReady[i.id].ready);
    const canCompose = !!onComposeOpen && !isProvisional(i) && (cReady || !!cEntry || (partsFrame && refineOpen)) && composeFocus !== i.id && !ancestorComposing(i);
    const titled = !!(i.label || "").trim();
    const status = unitStatus(i);
    if (partsFrame) {
      // STRUCTURE VIEW = MY WRITING SEEN STRUCTURALLY: the familiar writing actions only — Edit · Move (to a part of the essay) · Split · Join with
      // next · Delete, plus Move before / after. The old map's node operations (develop, add inside, title, sections, connect, span-split, join
      // with…, not sure, move out) stay in the data path and the essay frame; they are not permanently student-facing here.
      const nxt = nextParagraphOf(i.id);
      return (
      <div data-testid={`mm-actions-${i.id}`} className="mt-1.5 border-t border-stone-100 pt-1.5">
        {provisionalDecisions(i, status)}
        <div className="flex flex-wrap items-center gap-0.5">
          {canCompose ? (
            <button data-testid={`mm-compose-${i.id}`} title={WORK_WITH_COACH_TIP} onClick={(e) => { e.stopPropagation(); onComposeOpen(i.id); setSelected(null); }}
              className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded-full border border-emerald-400 text-emerald-800 hover:bg-emerald-50 transition-colors">
              <PenLine className="h-3 w-3" /> {cEntry ? "Keep working with coach" : "Work with coach"}
            </button>
          ) : null}
          <ActBtn testid={`mm-edit-${i.id}`} icon={Pencil} label="Edit text" title={EDIT_TEXT_TIP} onClick={() => startEdit(i)} />
          <ActBtn testid={`mm-move-${i.id}`} icon={CornerDownRight} label="Move" onClick={() => startMove("idea", i.id)} />
          {onSplitParagraph ? <ActBtn testid={`mm-split-${i.id}`} icon={Scissors} label="Split" onClick={() => startEdit(i, true)} /> : null}
          {onJoinNext && nxt ? <ActBtn testid={`mm-join-next-${i.id}`} icon={Merge} label="Join with next" onClick={() => { onJoinNext(i.id, nxt.id); setSelected(null); bump(); }} /> : null}
          <ActBtn testid={`mm-more-${i.id}`} icon={MoreHorizontal} label={showMore ? "Less" : "More"} onClick={() => setMoreFor(showMore ? null : i.id)} />
        </div>
        {showMore && (
          <div data-testid={`mm-more-actions-${i.id}`} role="group" aria-label="More actions for this part" onClick={(e) => e.stopPropagation()}
            className="mt-1 border-t border-stone-100 pt-1 flex flex-col gap-1">
            <ActGroup testid={`mm-more-structure-${i.id}`} title="Change its structure">
              {canUp({ type: "idea", id: i.id }) && <ActBtn testid={`mm-up-${i.id}`} icon={ChevronUp} label="Move before" onClick={() => { moveSibling({ type: "idea", id: i.id }, "up"); setMoreFor(null); }} />}
              {canDown({ type: "idea", id: i.id }) && <ActBtn testid={`mm-down-${i.id}`} icon={ChevronDown} label="Move after" onClick={() => { moveSibling({ type: "idea", id: i.id }, "down"); setMoreFor(null); }} />}
              {i.parent_id ? <ActBtn testid={`mm-pullout-${i.id}`} icon={ArrowUpRight} label="Move out of this part" onClick={() => { onPullOut({ type: "idea", id: i.id }); setSelected(null); setMoreFor(null); bump(); }} /> : null}
            </ActGroup>
            <div data-testid={`mm-more-delete-${i.id}`} className="flex items-center justify-end border-t border-stone-100 pt-1">
              <ActBtn testid={`mm-delete-${i.id}`} icon={Trash2} label="Delete" onClick={() => requestDelete("idea", i.id)} danger />
            </div>
          </div>
        )}
      </div>
      );
    }
    return (
    <div data-testid={`mm-actions-${i.id}`} className="mt-1.5 border-t border-stone-100 pt-1.5">
      {/* SIMPLIFY (UDWW 3C-5) — everything TWS proposed about this unit is decided HERE, once, when the card is selected */}
      {provisionalDecisions(i, status)}
      {/* UDWW Pass 3B / 3C-4b — TWS PROPOSES, the student decides: working the paragraph is a local, reversible next operation */}
      {canCompose && cReady && !cEntry ? (
        <p data-testid={`mm-compose-proposal-${i.id}`} className="mb-1 text-[11px] italic leading-snug text-emerald-800">
          This part is worked out enough to shape as a paragraph.
        </p>
      ) : null}
      {/* PRIMARY (3C-4b — simplified): EDIT · MOVE · MORE, plus the ONE developmental action that fits this unit right now */}
      <div className="flex flex-wrap items-center gap-0.5">
        {canCompose ? (
          <button data-testid={`mm-compose-${i.id}`} title={WORK_WITH_COACH_TIP} onClick={(e) => { e.stopPropagation(); onComposeOpen(i.id); setSelected(null); }}
            className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded-full border border-emerald-400 text-emerald-800 hover:bg-emerald-50 transition-colors">
            <PenLine className="h-3 w-3" /> {cEntry ? "Keep working with coach" : "Work with coach"}
          </button>
        ) : null}
        <ActBtn testid={`mm-edit-${i.id}`} icon={Pencil} label="Edit text" title={EDIT_TEXT_TIP} onClick={() => startEdit(i)} />
        <ActBtn testid={`mm-move-${i.id}`} icon={CornerDownRight} label="Move" onClick={() => startMove("idea", i.id)} />
        <ActBtn testid={`mm-more-${i.id}`} icon={MoreHorizontal} label={showMore ? "Less" : "More"} onClick={() => setMoreFor(showMore ? null : i.id)} />
      </div>
      {/* MORE — every existing operation, one tap away (nothing removed), organized as what the student is DOING: work on the writing ·
          change its structure · develop the thinking; Delete apart. Eligibility unchanged. Move lives on the primary row only (the local Move bar). */}
      {showMore && (
        <div data-testid={`mm-more-actions-${i.id}`} role="group" aria-label="More actions for this part" onClick={(e) => e.stopPropagation()}
          className="mt-1 border-t border-stone-100 pt-1 flex flex-col gap-1">
          <ActGroup testid={`mm-more-writing-${i.id}`} title="Work on the writing">
            {!isSec ? (
              <ActBtn testid={`mm-title-more-${i.id}`} icon={Tag} label={titled ? "Edit title" : "Give a title"} onClick={() => { setTitleVal((i.label || "").trim()); setTitleEdit(i.id); setMoreFor(null); }} />
            ) : null}
            {!isProvisional(i) && <ActBtn testid={`mm-separate-${i.id}`} icon={Scissors} label="Split" onClick={() => { setSep({ id: i.id, parts: ["", ""] }); setMoreFor(null); }} />}
            {!isProvisional(i) && onJoin && <ActBtn testid={`mm-join-${i.id}`} icon={Merge} label="Join with…" onClick={() => { startJoin(i.id); setMoreFor(null); }} />}
          </ActGroup>
          <ActGroup testid={`mm-more-structure-${i.id}`} title="Change its structure">
            {canUp({ type: "idea", id: i.id }) && <ActBtn testid={`mm-up-${i.id}`} icon={ChevronUp} label="Move before" onClick={() => { moveSibling({ type: "idea", id: i.id }, "up"); setMoreFor(null); }} />}
            {canDown({ type: "idea", id: i.id }) && <ActBtn testid={`mm-down-${i.id}`} icon={ChevronDown} label="Move after" onClick={() => { moveSibling({ type: "idea", id: i.id }, "down"); setMoreFor(null); }} />}
            {(i.parent_id || primaryQ(i) || i.under_thesis) && <ActBtn testid={`mm-pullout-${i.id}`} icon={ArrowUpRight} label="Move out of this part" onClick={() => { onPullOut({ type: "idea", id: i.id }); setSelected(null); setMoreFor(null); bump(); }} />}
            {onMakeSection && !isSec ? (
              <ActBtn testid={`mm-make-section-${i.id}`} icon={LayoutList} label="Make a section" onClick={() => setFuncPick(i.id)} />
            ) : null}
            {onMakeSection && !isSec ? (
              <ActBtn testid={`mm-make-subsection-${i.id}`} icon={IndentIncrease} label="Make a subsection" onClick={() => { onMakeSection(i.id, "subsection", ""); setSelected(null); setMoreFor(null); }} />
            ) : null}
            {onMakeSection && isSec ? (
              <ActBtn testid={`mm-identify-function-${i.id}`} icon={CompassIcon} label="What job does this part do?" onClick={() => setFuncPick(i.id)} />
            ) : null}
            {onMakeSection && isSec ? (
              <ActBtn testid={`mm-unsection-${i.id}`} icon={CornerDownRight} label="Change to an idea" onClick={() => { onMakeSection(i.id, "idea", ""); setSelected(null); setMoreFor(null); }} />
            ) : null}
          </ActGroup>
          <ActGroup testid={`mm-more-thinking-${i.id}`} title="Develop the thinking">
            <ActBtn testid={`mm-develop-${i.id}`} icon={Sparkles} label="Develop this idea" onClick={() => { onDevelop({ type: "idea", id: i.id, text: i.text }); setSelected(null); setMoreFor(null); }} />
            <ActBtn testid={`mm-add-under-${i.id}`} icon={Plus} label="Add idea inside" onClick={() => { setAdding({ kind: "beneath", parentType: "idea", parentId: i.id, text: "" }); setMoreFor(null); }} />
            <ActBtn testid={`mm-connect-${i.id}`} icon={Link2} label="Connect to…" onClick={() => { startConnect(i.id); setMoreFor(null); }} />
            {SHOW_GENERIC_ADD_QUESTION && <ActBtn testid={`mm-add-q-under-${i.id}`} icon={GitBranch} label="Question" onClick={() => setAdding({ kind: "question-under", parentType: "idea", parentId: i.id, text: "" })} />}
            <ActBtn testid={`mm-uncertain-${i.id}`} icon={HelpCircle} label={i.status === "need_to_figure_out" ? "Clearer now" : "Not sure"} onClick={() => { onToggleUncertain(i); setSelected(null); setMoreFor(null); }} />
          </ActGroup>
          <div data-testid={`mm-more-delete-${i.id}`} className="flex items-center justify-end border-t border-stone-100 pt-1">
            <ActBtn testid={`mm-delete-${i.id}`} icon={Trash2} label="Delete" onClick={() => requestDelete("idea", i.id)} danger />
          </div>
        </div>
      )}
    </div>
    );
  };

  // Name a section's role (introduction / body / conclusion / other). Reused to CREATE a section with its
  // role in one step (usability pass — no separate "identify function" step) and to change an existing one.
  const funcPicker = (i) => (
    <div data-testid={`mm-func-picker-${i.id}`} onClick={(e) => e.stopPropagation()} className="mt-1.5 border-t border-stone-100 pt-1.5">
      <p className="text-[10px] uppercase tracking-wider text-stone-400 mb-1">{isSectionNode(i) ? "What job does this section do?" : "Make this a section — what job does it do?"}</p>
      <div className="flex flex-wrap gap-1">
        {[["introduction", "Introduction"], ["body", "A body section"], ["conclusion", "Conclusion"], ["other", "Something else"]].map(([val, lbl]) => (
          <button key={val} data-testid={`mm-func-${val}-${i.id}`}
            onClick={() => { onMakeSection(i.id, i.node_kind === "subsection" ? "subsection" : "section", val); setFuncPick(null); setMoreFor(null); }}
            className={`text-[11px] px-2 py-1 rounded border transition-colors ${(i.composition_function || "body") === val ? "border-[#8C3A2A] bg-[#8C3A2A]/10 text-[#8C3A2A]" : "border-stone-200 text-stone-600 hover:border-[#8C3A2A]"}`}>
            {lbl}
          </button>
        ))}
        <button onClick={() => setFuncPick(null)} className="text-[11px] px-2 py-1 text-stone-400">Cancel</button>
      </div>
    </div>
  );

  const questionActions = (q) => (
    <div data-testid={`mm-qactions-${q.id}`} className="mt-1.5 flex flex-wrap items-center gap-0.5 border-t border-[#8C3A2A]/10 pt-1.5">
      <button data-testid={`mm-workon-${q.id}`} onClick={(e) => { e.stopPropagation(); onWorkOn(q); setSelected(null); }} disabled={coachBusy}
        className="inline-flex items-center gap-1 text-[11px] font-medium text-white bg-[#8C3A2A] px-2 py-1 rounded hover:bg-[#6B2C20] disabled:opacity-40">
        <CompassIcon className="h-3 w-3" /> Develop my thinking about this
      </button>
      <ActBtn testid={`mm-add-answer-${q.id}`} icon={MessageSquareText} label="Answer" onClick={() => setAdding({ kind: "answer", parentType: "question", parentId: q.id, text: "" })} />
      {SHOW_GENERIC_ADD_QUESTION && <ActBtn testid={`mm-add-subq-${q.id}`} icon={GitBranch} label="Subquestion" onClick={() => setAdding({ kind: "subq", parentType: "question", parentId: q.id, text: "" })} />}
      <ActBtn testid={`mm-qedit-${q.id}`} icon={Pencil} label="Edit text" title={EDIT_TEXT_TIP} onClick={() => setEditing({ type: "question", id: q.id, text: q.text })} />
      <ActBtn testid={`mm-qmove-${q.id}`} icon={CornerDownRight} label="Make part of…" onClick={() => startMove("question", q.id)} />
      {canUp({ type: "question", id: q.id }) && <ActBtn testid={`mm-qup-${q.id}`} icon={ChevronUp} label="Up" onClick={() => moveSibling({ type: "question", id: q.id }, "up")} />}
      {canDown({ type: "question", id: q.id }) && <ActBtn testid={`mm-qdown-${q.id}`} icon={ChevronDown} label="Down" onClick={() => moveSibling({ type: "question", id: q.id }, "down")} />}
      {isChildQ(q.id) && <ActBtn testid={`mm-qpullout-${q.id}`} icon={ArrowUpRight} label="Pull out" onClick={() => { onPullOut({ type: "question", id: q.id }); setSelected(null); bump(); }} />}
      <ActBtn testid={`mm-qdelete-${q.id}`} icon={Trash2} label="" onClick={() => requestDelete("question", q.id)} danger />
    </div>
  );

  // EXPANSIVE EDITING: reading = compact card; writing = the unit opens to its full width and grows with the text
  const editor = () => (
    <div data-testid="mm-editor" onClick={(e) => e.stopPropagation()}>
      {editing.type === "idea" && !partsFrame && (
        <div className="mb-1.5">
          <label className="block font-mono text-[8px] uppercase tracking-[0.14em] text-stone-400 mb-0.5">Short label (your words · optional)</label>
          <input data-testid="mm-label-input" {...otNoPaste} value={editing.label || ""}
            onChange={(e) => setEditing({ ...editing, label: e.target.value })}
            onKeyDown={entryKeys(saveEditing, cancelEditing)}
            placeholder="A few words to name this card…" maxLength={80}
            className="w-full text-[12.5px] font-semibold text-stone-900 outline-none border border-[#8C3A2A]/30 rounded px-2 py-1 bg-white" />
        </div>
      )}
      <textarea autoFocus={editing.type !== "idea" || partsFrame} data-testid="mm-edit-input" {...otNoPaste} value={editing.text} rows={4}
        onBlur={() => { if (editing.type === "idea") { clearTimeout(autosaveTimer.current); autosaveEditing(); } }}
        ref={(el) => { editRef.current = el; autoGrow(el, EDIT_MIN_PX); }}
        onChange={(e) => { changeEditing(e.target.value); autoGrow(e.target, EDIT_MIN_PX); }}
        onInput={(e) => autoGrow(e.target, EDIT_MIN_PX)}
        onKeyDown={entryKeys(saveEditing, cancelEditing)}
        className="block w-full min-w-0 resize-none min-h-[140px] max-h-[60vh] leading-relaxed text-[13.5px] text-stone-800 outline-none border border-[#8C3A2A]/40 focus:border-[#8C3A2A] rounded-md px-3 py-2.5 bg-white transition-colors" />
      <div className="flex flex-wrap items-center justify-end gap-2 mt-1">
        <span data-testid="mm-edit-hint" className="mr-auto text-[10px] text-stone-400">
          {partsFrame && splitHint ? "Click where the new paragraph should begin, then Split here" : ({ editing: "Editing\u2026", saving: "Saving\u2026", saved: "Saved" }[editing.saveState] || "Saves as you write \u00b7 \u2318/Ctrl+Return saves now")}
        </span>
        {partsFrame && editing.type === "idea" && onSplitParagraph ? (
          <button data-testid="mm-split-here" onClick={splitHere} title="Make two paragraphs of this one, at the cursor. Your words stay exactly as they are."
            className={`inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded border transition-colors ${splitHint ? "border-[#8C3A2A] text-[#8C3A2A] bg-[#8C3A2A]/5" : "border-stone-200 text-stone-500 hover:text-[#8C3A2A] hover:border-[#8C3A2A]/50"}`}>
            <Scissors className="h-3 w-3" /> Split here
          </button>
        ) : null}
        <button data-testid="mm-edit-cancel" onClick={cancelEditing} className="text-[11px] text-stone-500 px-2">Cancel</button>
        <button data-testid="mm-edit-save" onClick={saveEditing} className="inline-flex items-center gap-1 bg-[#8C3A2A] text-white text-[11px] px-3 py-1 rounded"><Check className="h-3 w-3" /> Save now</button>
      </div>
    </div>
  );

  const sepEditor = (i) => (
    <div data-testid={`mm-separate-${i.id}`} onClick={(e) => e.stopPropagation()}>
      {/* REPAIR §4: the SOURCE idea stays visible + selectable/copyable while you differentiate it; it is not
          destroyed until you confirm the split. */}
      <div className="mb-2 rounded border border-stone-200 bg-stone-50 px-2 py-1.5">
        <p className="font-mono text-[8px] uppercase tracking-[0.14em] text-stone-400 mb-0.5">This unit (its words stay yours — the earlier whole is kept as history)</p>
        <p data-testid={`mm-sep-source-${i.id}`} className="text-[12.5px] text-stone-700 leading-snug select-text">{i.text}</p>
      </div>
      <p className="text-[11px] text-stone-500 mb-1.5">Pull this into separate ideas &mdash; one per line, each in the unit&rsquo;s own words (copy from above). Words you leave out stay with this unit.</p>
      {sep.parts.map((p, idx) => (
        <input key={idx} data-testid={`mm-sep-part-${idx}`} {...otNoPaste} value={p}
          onChange={(e) => { const parts = [...sep.parts]; parts[idx] = e.target.value; setSep({ ...sep, parts }); }}
          placeholder={`Idea ${idx + 1}`} className="w-full text-[12.5px] px-2 py-1.5 mb-1.5 border border-stone-300 rounded outline-none focus:border-[#8C3A2A]" />
      ))}
      <div className="flex items-center justify-between">
        <button onClick={() => setSep({ ...sep, parts: [...sep.parts, ""] })} className="text-[11px] text-[#8C3A2A]">+ another</button>
        <div className="flex gap-2">
          <button onClick={() => setSep(null)} className="text-[11px] text-stone-500 px-2">Cancel</button>
          <button data-testid="mm-sep-commit" onClick={commitSep} disabled={sep.parts.filter((x) => x.trim()).length < 2}
            className="inline-flex items-center gap-1 bg-[#8C3A2A] text-white text-[11px] px-3 py-1 rounded disabled:opacity-40"><Scissors className="h-3 w-3" /> Split</button>
        </div>
      </div>
    </div>
  );

  // inline "add" input shown beneath the node whose add-control was tapped
  const addInput = (kind, parentId) => {
    if (!adding || adding.kind !== kind || adding.parentId !== parentId) return null;
    const ph = kind === "subq" ? "A more specific question inside this one…" : kind === "question-under" ? "A new question raised by this idea…" : kind === "answer" ? "Your answer to this question…" : "A detail, example, or reason under this idea…";
    return (
      <div data-testid={`mm-add-input-${parentId}`} className="ml-4 mt-1.5 rounded-lg border border-[#8C3A2A]/30 bg-white p-2" onClick={(e) => e.stopPropagation()}>
        <textarea autoFocus data-testid="mm-add-node-input" {...otNoPaste} value={adding.text} rows={2} placeholder={ph}
          onChange={(e) => setAdding({ ...adding, text: e.target.value })}
          onKeyDown={(e) => { if (e.key === "Escape") setAdding(null); if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) commitAdd(); }}
          className="w-full resize-none text-[12.5px] text-stone-800 outline-none" />
        <div className="flex justify-end gap-2 mt-1">
          <button onClick={() => setAdding(null)} className="text-[11px] text-stone-500 px-2">Cancel</button>
          <button data-testid="mm-add-node-save" onClick={commitAdd} disabled={!adding.text.trim()} className="inline-flex items-center gap-1 bg-[#8C3A2A] text-white text-[11px] px-3 py-1 rounded disabled:opacity-40"><Plus className="h-3 w-3" /> Add</button>
        </div>
      </div>
    );
  };

  // ROLE → only a left-rule accent now (SIMPLIFY, UDWW 3C-5: no "part of the answer / main idea / part of ↑" tags — the
  // headings and indentation say where a unit sits). The role names are kept for the renderers' semantics.
  const hasThesis = !!(thesis && thesis.trim());
  const ROLE = { answer: {}, subidea: {}, loose: {}, root: {} };
  // UNIFIED WORKSPACE — visual epistemic status of a unit (established / TWS-provisional / unresolved). ONE restrained word
  // on the card ("provisional"); what it means and the decision (keep / not this) live in the card's action panel.
  const STATUS_CHIP = {
    provisional_unit: { text: "provisional", decision: "The Writing Studio reads this as a distinct part inside your writing.", reject: "not a separate part",
      title: "The Writing Studio read this as a distinct part inside your writing. Tap the card to keep it, or say it is not a separate part." },
    provisional_place: { text: "provisional", decision: "The Writing Studio put this here for now.", reject: "not here",
      title: "The Writing Studio put this here for now. Tap the card to keep it, move it, or take it back out." },
    // UDWW 3C-1 — a loose unit TWS provisionally reads as a part of the emerging answer (not the student's structure until kept)
    provisional_answer: { text: "provisional", decision: "The Writing Studio reads this as a part of your emerging answer.", reject: "not here",
      title: "The Writing Studio reads this as a part of your emerging answer. Tap the card to keep it there, or send it back to Thoughts I'm still developing — your words stay exactly as they are." },
  };
  const chipFor = (i, status) => (status === "provisional_place" && !i.parent_id && i.under_thesis) ? STATUS_CHIP.provisional_answer : STATUS_CHIP[status];
  // UDWW 3C-1 — TWS-provisional RELATIONS: one representation of one relationship, on the SOURCE unit only
  const provisionalRelsFrom = (id) => (rels || []).filter((r) => (r.provenance === "tws_provisional" || r.provenance === "tws_inferred") && r.from_id === id);
  // STUDENT-OWNED relations from this unit (made, described or kept by the student): the same local row, ordinary — no "TWS sees"
  const studentRelsFrom = (id) => (rels || []).filter((r) => !(r.provenance === "tws_provisional" || r.provenance === "tws_inferred") && r.from_id === id);
  const unitTextOf = (id) => { const u = items.find((x) => x.id === id); return u ? ((u.label || "").trim() || (u.text || "")) : ""; };

  // TWS-PROVISIONAL STRUCTURE ABOUT THIS UNIT — decided in ONE place, when the card is selected: a provisional part / place
  // (keep · not this), TWS's functional read (keep as title · not this), suggested connections (keep · not this).
  const provisionalDecisions = (i, status) => {
    const chip = status.startsWith("provisional") ? chipFor(i, status) : null;
    const role = (i.provisional_role || "").trim();
    const prels = provisionalRelsFrom(i.id);
    const srels = studentRelsFrom(i.id);
    if (!chip && !role && prels.length === 0 && srels.length === 0) return null;
    const small = "inline-flex items-center gap-0.5 text-[10.5px] px-1.5 py-0.5 rounded-full border transition-colors";
    return (
      <div data-testid={`mm-provisional-actions-${i.id}`} className="mb-1.5 space-y-1" onClick={(e) => e.stopPropagation()}>
        {chip ? (
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-stone-600">
            <span className="italic">{chip.decision}</span>
            <button data-testid={`mm-keep-${i.id}`} onClick={() => onAdoptProvisional && onAdoptProvisional(i.id)}
              className={`${small} font-semibold text-emerald-700 border-emerald-300 hover:bg-emerald-50`}><Check className="h-3 w-3" /> keep</button>
            <button data-testid={`mm-reject-${i.id}`} onClick={() => onRejectProvisional && onRejectProvisional(i.id, status)}
              className={`${small} text-stone-500 border-stone-300 hover:bg-stone-50`}><X className="h-3 w-3" /> {chip.reject}</button>
          </div>
        ) : null}
        {role ? (
          /* UDWW 3C-1 — TWS's provisional FUNCTIONAL read of this unit; dismissing it rejects the interpretation, never the words */
          <div data-testid={`mm-role-${i.id}`} className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-stone-600 min-w-0">
            <span className="italic min-w-0 truncate">reads this as: {role}</span>
            {!isSectionNode(i) ? (
              <button data-testid={`mm-role-keep-${i.id}`} aria-label="Keep as this paragraph's title" title="Keep this as the paragraph's working title — it becomes yours."
                onClick={() => keepRoleAsTitle(i)} className={`${small} font-semibold text-emerald-700 border-emerald-300 hover:bg-emerald-50`}><Check className="h-3 w-3" /> keep as title</button>
            ) : null}
            <button data-testid={`mm-role-dismiss-${i.id}`} aria-label="Not what this is doing" title="Not what this is doing"
              onClick={() => onDismissRole && onDismissRole(i.id)} className={`${small} text-stone-500 border-stone-300 hover:bg-stone-50`}><X className="h-3 w-3" /> not this</button>
          </div>
        ) : null}
        {srels.map((r) => (
          /* a STUDENT-OWNED relation: ordinary text, solid rule, no tentative language; Remove stays the existing route */
          <div key={r.id} data-testid={`mm-rel-row-${r.id}`} data-owner="student" className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-stone-700 min-w-0 border-l-2 border-[#8C3A2A]/50 pl-2">
            <span data-testid={`mm-rel-row-text-${r.id}`} className="min-w-0 break-words">{r.relation_text || r.label || "connects to"} &ldquo;{unitTextOf(r.to_id)}&rdquo;</span>
            {onDeleteRel ? <button data-testid={`mm-rel-row-remove-${r.id}`} onClick={() => { onDeleteRel(r.id); bump(); }} aria-label="Remove this connection"
              className={`${small} text-stone-400 border-stone-200 hover:text-rose-500 hover:border-rose-200`}><X className="h-3 w-3" /> remove</button> : null}
          </div>
        ))}
        {prels.map((r) => (
          /* UDWW 3C-1 — a TWS-PROPOSED RELATION from this unit (source only): tentative wording, dashed, italic. KEEP makes it the student's (existing
             adoption route); NOT THIS removes the hypothesis, never a unit. Viewing it changes nothing. */
          <div key={r.id} data-testid={`mm-prov-rel-${r.id}`} data-owner="tws" className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-stone-500 min-w-0 border-l-2 border-dashed border-[#8C3A2A]/30 pl-2">
            <span data-testid={`mm-prov-rel-text-${r.id}`} className="min-w-0 break-words italic"><span className="not-italic text-stone-400">TWS sees:</span> {r.relation_text || r.label || "connects to"} &ldquo;{unitTextOf(r.to_id)}&rdquo;</span>
            <button data-testid={`mm-prov-rel-keep-${r.id}`} onClick={() => onAdoptProvisionalRel && onAdoptProvisionalRel(r.id)}
              className={`${small} font-semibold text-emerald-700 border-emerald-300 hover:bg-emerald-50`}><Check className="h-3 w-3" /> keep</button>
            <button data-testid={`mm-prov-rel-reject-${r.id}`} onClick={() => onRejectProvisionalRel && onRejectProvisionalRel(r.id)}
              className={`${small} text-stone-500 border-stone-300 hover:bg-stone-50`}><X className="h-3 w-3" /> not this</button>
          </div>
        ))}
      </div>
    );
  };

  // ---- recursive idea node ----
  // SIMPLIFY (UDWW 3C-5): a unit is its writing — a left rule, its title, its text, what the conversation added; children
  // indented beneath it. No box-in-box, no role tags, no function chips, at most ONE quiet status word. Everything TWS
  // proposed about it (part / place / read / connection) is decided in the action panel when the card is selected.
  const renderIdeaNode = (i, role) => {
    const isSel = selected && selected.type === "idea" && selected.id === i.id;
    const isEditing = editing && editing.type === "idea" && editing.id === i.id;
    const isSep = sep && sep.id === i.id;
    const focused = focusIds.includes(i.id);
    const uncertain = i.status === "need_to_figure_out";
    const isMoveTgt = mode && mode.kind === "move" && canDrop({ type: mode.srcType, id: mode.srcId }, { type: "idea", id: i.id });
    const isConnTgt = (mode && mode.kind === "connect" && canBeConnectTarget(i.id)) || (mode && mode.kind === "join" && canBeJoinTarget(i.id));
    const isDropTgt = dropTarget && dropTarget.type === "idea" && dropTarget.id === i.id;
    const isDragging = drag && drag.type === "idea" && drag.id === i.id;
    const isMovingSrc = inMove && mode.srcType === "idea" && mode.srcId === i.id;
    const status = unitStatus(i);
    const provisional = status.startsWith("provisional");
    const uv = unitView(i);
    // AUTHENTIC REPAIR 1 — IN-PLACE DIFFERENTIATION: TWS-provisional sub-units that are exact spans of this unit's text are
    // rendered INSIDE the text (each span its own manipulable part, the remaining words between them) — never repeated below.
    const spanKids = childIdeas(i.id).filter((c) => c.provenance === "tws_provisional");
    const segments = (!isEditing && !isSep) ? segmentUnitText(uv.head, spanKids) : null;
    const inlineIds = new Set((segments || []).filter((s) => s.unit).map((s) => s.unit.id));
    const railKids = mergedIdeaChildren(i.id).filter((c) => !(c.kind === "i" && inlineIds.has(c.node.id)));
    // UDWW Pass 3B — compose locally: this unit's process state (never its text) + whether it is the focused writing area
    const cEntry = compose && compose[i.id];
    const composing = !!(cEntry && cEntry.status === "composing");
    const hasDraft = !!(cEntry && cEntry.status === "developing" && ((cEntry.turns || 0) > 0 || cEntry.entered_by === "student_prose"));
    const cReady = !!(composeReady && composeReady[i.id] && composeReady[i.id].ready);
    const isComposeFocus = composeFocus === i.id;
    const titled = !!(i.label || "").trim();
    // ROUTING — a QUIET TWS-READ HEADING over an untitled major part (what this part is doing, in plain words); no buttons on
    // the face — keep as title / not this live in the card's panel. Never over a student title; never on nested / loose units.
    const twsHeading = (!titled && !isSectionNode(i) && role === "root") ? ((i.provisional_plain || i.provisional_role || "").trim()) : "";
    const word = "font-mono text-[8.5px] uppercase tracking-[0.14em] rounded px-1 py-px";
    const statusWord = composing ? (
      <span data-testid={`mm-composing-${i.id}`} title="You are refining this paragraph. Its ideas stay right here." className={`${word} text-emerald-800 border border-emerald-300 bg-emerald-50 inline-flex items-center gap-0.5`}><PenLine className="h-2 w-2" /> refining</span>
    ) : hasDraft ? (
      <span data-testid={`mm-draft-${i.id}`} title="You have started shaping this paragraph. Pick it up again any time." className={`${word} text-emerald-800/80 border border-emerald-200`}>in progress</span>
    ) : provisional ? (
      <span data-testid={`mm-provisional-${i.id}`} title={chipFor(i, status).title} className={`${word} text-[#8C3A2A]/70 border border-dashed border-[#8C3A2A]/40`}>{chipFor(i, status).text}</span>
    ) : (cReady && !cEntry && !i.parent_id) ? (
      <span data-testid={`mm-ready-${i.id}`} title="This part is worked out enough to shape as a paragraph — if you want to." className={`${word} text-emerald-700/70 border border-dashed border-emerald-300`}>paragraph-ready</span>
    ) : null;
    return (
      <motion.div layout={dragging ? false : "position"} key={i.id} className="relative flex-1 min-w-0">
        <motion.div layout={!dragging} {...nodeDragProps("idea", i.id)}
          ref={registerNode(i.id)}
          data-testid={`mm-idea-${i.id}`}
          data-status={status}
          data-writing={isEditing ? "editing" : isComposeFocus ? "composing" : undefined}
          data-compose={composing ? "composing" : hasDraft ? "draft" : cReady ? "ready" : undefined}
          onClick={(e) => { e.stopPropagation(); if (!(isEditing || isSep)) nodeClick("idea", i.id); }}
          className={`group/node relative z-10 border-l-2 pl-3 pr-1.5 py-1.5 rounded-r-md ${partsFrame ? "" : "cursor-grab active:cursor-grabbing"} transition-colors w-full ${(isEditing || isComposeFocus) ? "max-w-full cursor-default" : "max-w-[640px]"}
            ${provisional ? "border-dashed border-[#8C3A2A]/50" : uncertain ? "border-dashed border-amber-300" : isSectionNode(i) ? "border-[#8C3A2A]" : isSel ? "border-[#8C3A2A]" : focused ? "border-[#8C3A2A]/60" : "border-transparent hover:border-stone-300"}
            ${isComposeFocus ? "bg-emerald-50/40 ring-1 ring-emerald-300/60" : isSel ? "bg-white shadow-sm" : focused ? "bg-[#8C3A2A]/[0.04]" : ""}
            ${(isMoveTgt || isConnTgt || isDropTgt) ? "ring-2 ring-emerald-400 border-emerald-400 rounded-md" : ""}
            ${isMoveTgt ? "cursor-pointer hover:bg-emerald-50/60" : ""}
            ${isDropTgt ? "bg-emerald-50 shadow" : ""}
            ${isMovingSrc ? "opacity-60 ring-2 ring-emerald-500 ring-offset-1" : ""}
            ${inMove && !isMoveTgt && !isMovingSrc ? "opacity-40" : ""}
            ${dimForFocus && !focused ? "opacity-45" : ""}
            ${isDragging ? "opacity-40" : ""}`}>
          {isMoveTgt && (
            <span data-testid={`mm-place-here-${i.id}`} className="absolute -top-2 -right-2 z-30 inline-flex items-center gap-0.5 rounded-full bg-emerald-500 text-white text-[9px] font-semibold px-1.5 py-0.5 shadow">
              <CornerDownRight className="h-2.5 w-2.5" /> Place here
            </span>
          )}
          {isEditing ? editor() : isSep ? sepEditor(i) : (
            <>
              {/* HEAD LINE: section identity / working title · one quiet status word · re-look · (title editor) */}
              {(isSectionNode(i) || titled || twsHeading || statusWord || i.stale_relative_to_whole || titleEdit === i.id) ? (
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 mb-0.5 min-w-0">
                  {isSectionNode(i) ? (
                    <span data-testid={`mm-section-id-${i.id}`} className="font-mono text-[9px] uppercase tracking-[0.16em] text-[#8C3A2A]">{sectionIdentity(i)}</span>
                  ) : null}
                  {!isSectionNode(i) && titleEdit === i.id ? (
                    <span className="flex items-center gap-1 min-w-0 flex-1" onClick={(e) => e.stopPropagation()}>
                      <input data-testid={`mm-title-input-${i.id}`} autoFocus {...otNoPaste} value={titleVal} maxLength={80}
                        onChange={(e) => setTitleVal(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") saveTitle(i); if (e.key === "Escape") setTitleEdit(null); }}
                        placeholder="What is this paragraph doing? (your words)"
                        className="w-56 max-w-full rounded border border-stone-300 px-1.5 py-0.5 text-[12px] outline-none focus:border-[#8C3A2A] bg-white" />
                      <button data-testid={`mm-title-save-${i.id}`} onClick={() => saveTitle(i)} className="text-[10px] font-semibold text-[#8C3A2A]">save</button>
                    </span>
                  ) : titled && !isSectionNode(i) ? (
                    <span className="group/title inline-flex items-baseline gap-1.5 min-w-0">
                      <p data-testid={`mm-label-${i.id}`} className="text-[13px] font-semibold leading-snug text-stone-900" title={i.text}>{i.label.trim()}</p>
                      <button data-testid={`mm-title-btn-${i.id}`}
                        onClick={(e) => { e.stopPropagation(); setTitleVal((i.label || "").trim()); setTitleEdit(i.id); }}
                        className="inline-flex items-center gap-0.5 text-[9px] text-stone-400 opacity-0 group-hover/node:opacity-100 focus:opacity-100 hover:text-[#8C3A2A] transition-opacity"
                        title="Change this paragraph's working title — your representation of what it is doing. The text stays exactly as written.">
                        <Tag className="h-2.5 w-2.5" /> edit title
                      </button>
                    </span>
                  ) : twsHeading ? (
                    <p data-testid={`mm-part-heading-${i.id}`} title="How The Writing Studio reads this part — a starting shape, yours to rename (tap the card: keep as title · not this)"
                      data-owner="tws" className="text-[12.5px] font-medium leading-snug text-stone-500 italic border-b border-dashed border-stone-300/80">
                      <span className="not-italic font-normal text-[10px] text-stone-400 mr-1">TWS reads this as</span>{twsHeading.charAt(0).toLowerCase() + twsHeading.slice(1)}</p>
                  ) : null}
                  {statusWord}
                  {i.stale_relative_to_whole ? (
                    <span data-testid={`mm-stale-${i.id}`} title="You developed this relative to an earlier main idea — worth a second look now that it has changed."
                      className={`${word} text-amber-700/80 border border-amber-300`}>re-look?</span>
                  ) : null}
                </div>
              ) : null}
              <div className="flex items-start gap-1.5">
                {uncertain && <HelpCircle className="h-3.5 w-3.5 text-amber-500 mt-0.5 shrink-0" title="Still figuring this out" />}
                <div className="min-w-0 flex-1">
                  {isComposeFocus ? (
                    /* UDWW Pass 3B — THE STUDENT WRITES IN THE STRUCTURE: the writing area replaces the card body (one text, no duplicate) */
                    <ComposeUnit unit={i} view={composeView} busy={composeBusy}
                      onTurn={(text, kind, message) => onComposeTurn && onComposeTurn(i.id, text, kind, message)}
                      onReturn={() => onComposeReturn && onComposeReturn(i.id)} />
                  ) : expanded[i.id] === false ? (
                    !titled && <p className="text-[13px] leading-relaxed text-stone-800" title={i.text}>{shortOf(i)}</p>
                  ) : (
                    <>
                      {segments ? (
                        <div data-testid={`mm-unit-segmented-${i.id}`} className="space-y-1.5">
                          {segments.map((s, k) => s.unit ? (
                            <div key={s.unit.id} data-testid={`mm-unit-seg-${i.id}-${k}`} className="flex items-stretch" onClick={(e) => e.stopPropagation()}>
                              {renderIdeaNode(s.unit, "subidea")}
                            </div>
                          ) : (
                            <p key={k} data-testid={`mm-unit-seg-${i.id}-${k}`} className={`text-[13px] leading-relaxed whitespace-pre-line ${titled ? "text-stone-700" : "text-stone-800"}`}>{s.text}</p>
                          ))}
                        </div>
                      ) : (
                        <p data-testid={`mm-unit-text-${i.id}`} className={`text-[13px] leading-relaxed whitespace-pre-line ${titled ? "text-stone-700" : "text-stone-800"}`} title={i.text}>{uv.head}</p>
                      )}
                      {/* UNIFIED WORKSPACE: what the student said in the conversation that DEVELOPS this unit grows the unit itself */}
                      {uv.additions.map((t, k) => (
                        <p key={k} data-testid={`mm-dev-${i.id}-${k}`} className="text-[13px] leading-relaxed whitespace-pre-line text-stone-700 mt-1.5">{t}</p>
                      ))}
                      {/* STRUCTURED CONTENT — the teaching projection (ot.consolidated.teaching) is no longer narrated on the card ("NOW: … → …"): the
                          progress block beside the conversation says, in plain words, what is being worked on; the data path is unchanged */}
                      {uv.history.length > 0 && (
                        <button data-testid={`mm-history-${i.id}`} onClick={(e) => { e.stopPropagation(); setHistOpen((h) => ({ ...h, [i.id]: !h[i.id] })); }}
                          className="mt-1 text-[10px] text-stone-400 hover:text-[#8C3A2A]">{histOpen[i.id] ? "hide earlier wording" : `earlier wording (${uv.history.length})`}</button>
                      )}
                      {histOpen[i.id] && uv.history.map((t, k) => (
                        <p key={`h${k}`} data-testid={`mm-history-line-${i.id}-${k}`} className="text-[11.5px] leading-snug text-stone-400 line-through mt-0.5 pl-2">{t}</p>
                      ))}
                    </>
                  )}
                </div>
              </div>
              {/* UDWW 3C-4b — WORKING TITLE, first-class: once a unit is paragraph-like and untitled, invite the student to say what
                  this paragraph is DOING (their representation of the part as a whole). Never asked of tiny units; never authored by TWS. */}
              {!partsFrame && !isEditing && !isComposeFocus && !isSectionNode(i) && !titled && titleEdit !== i.id && isParagraphLike(i) ? (
                <button data-testid={`mm-title-invite-${i.id}`}
                  onClick={(e) => { e.stopPropagation(); setTitleVal(""); setTitleEdit(i.id); }}
                  className="mt-1.5 inline-flex items-center gap-1 text-[10.5px] text-[#8C3A2A]/80 hover:text-[#8C3A2A]">
                  <Tag className="h-3 w-3" /> Working title — what is this paragraph doing?
                </button>
              ) : null}
              {(titled || i.text !== shortOf(i)) && !isComposeFocus && (
                <button data-testid={`mm-expand-${i.id}`} onClick={(e) => { e.stopPropagation(); toggleExpand(i.id); }}
                  className="mt-0.5 text-[10px] text-stone-400 opacity-0 group-hover/node:opacity-100 focus:opacity-100 hover:text-[#8C3A2A] transition-opacity">{expanded[i.id] === false ? "full" : "less"}</button>
              )}
              {isSectionNode(i) && functionText(i.composition_function) ? (
                <p data-testid={`mm-section-fn-${i.id}`} data-owner="student" className="mt-1 text-[10.5px] text-stone-500 leading-snug">{functionText(i.composition_function)}</p>   /* the student set this job: ordinary, not tentative */
              ) : null}
              {isSel && !mode && funcPick === i.id ? funcPicker(i) : (isSel && !mode && ideaActions(i))}
            </>
          )}
        </motion.div>
        {isMovingSrc && partsFrame ? localMoveBar(i) : null}
        {addInput("beneath", i.id)}
        {addInput("question-under", i.id)}
        {mergedIdeaChildren(i.id).length > 0 && railKids.length > 0 && (
          <div className="ml-5 mt-1 space-y-1.5">
            {railKids.map((c) => (
              <div key={c.node.id}>
                <DropLine target={{ type: "idea", id: i.id }} beforeId={c.node.id} />
                {c.kind === "q" ? renderQuestionNode(c.node, 1) : renderIdeaNode(c.node, "subidea")}
              </div>
            ))}
            <DropLine target={{ type: "idea", id: i.id }} beforeId="__END__" />
          </div>
        )}
      </motion.div>
    );
  };

  // ---- recursive question node (a strand) ----
  const renderQuestionNode = (q, depth) => {
    const isSel = selected && selected.type === "question" && selected.id === q.id;
    const isEditing = editing && editing.type === "question" && editing.id === q.id;
    const focused = focusQIds.includes(q.id);
    const isMoveTgt = mode && mode.kind === "move" && canDrop({ type: mode.srcType, id: mode.srcId }, { type: "question", id: q.id });
    const isDropTgt = dropTarget && dropTarget.type === "question" && dropTarget.id === q.id;
    const isDragging = drag && drag.type === "question" && drag.id === q.id;
    const isMovingSrc = inMove && mode.srcType === "question" && mode.srcId === q.id;
    const kids = mergedQChildren(q.id);
    return (
      <motion.div layout={dragging ? false : "position"} key={q.id} className="relative flex-1 min-w-0">
        <motion.div layout={!dragging} {...nodeDragProps("question", q.id)}
          ref={registerNode(q.id)}
          data-testid={`mm-question-${q.id}`}
          data-writing={isEditing ? "editing" : undefined}
          onClick={(e) => { e.stopPropagation(); if (!isEditing) nodeClick("question", q.id); }}
          className={`group/node relative z-10 border-l-2 border-[#8C3A2A]/40 pl-3 pr-1.5 py-1.5 rounded-r-md cursor-grab active:cursor-grabbing transition-colors ${isEditing ? "w-full max-w-full cursor-default" : "max-w-[480px]"}
            ${focused ? "border-[#8C3A2A] bg-[#8C3A2A]/[0.04]" : isSel ? "border-[#8C3A2A] bg-white shadow-sm" : "hover:border-[#8C3A2A]/70"}
            ${(isMoveTgt || isDropTgt) ? "ring-2 ring-emerald-400 border-emerald-400 rounded-md" : ""}
            ${isMoveTgt ? "cursor-pointer hover:bg-emerald-50/60" : ""}
            ${isDropTgt ? "bg-emerald-50 shadow" : ""}
            ${isMovingSrc ? "opacity-60 ring-2 ring-emerald-500 ring-offset-1" : ""}
            ${inMove && !isMoveTgt && !isMovingSrc ? "opacity-40" : ""}
            ${dimForFocus && !focused ? "opacity-45" : ""}
            ${isDragging ? "opacity-40" : ""}`}>
          {isMoveTgt && (
            <span data-testid={`mm-place-here-${q.id}`} className="absolute -top-2 -right-2 z-30 inline-flex items-center gap-0.5 rounded-full bg-emerald-500 text-white text-[9px] font-semibold px-1.5 py-0.5 shadow">
              <CornerDownRight className="h-2.5 w-2.5" /> Place here
            </span>
          )}
          {isEditing ? editor() : (
            <>
              <div className="flex items-start gap-1.5">
                <GitBranch className="h-3.5 w-3.5 text-[#8C3A2A] mt-0.5 shrink-0" />
                <p className="text-[13px] font-semibold leading-snug text-stone-800">{q.text}</p>
              </div>
              {isSel && !mode && questionActions(q)}
            </>
          )}
        </motion.div>
        {addInput("subq", q.id)}
        {addInput("answer", q.id)}
        {kids.length > 0 && (
          <div className="ml-5 mt-1 space-y-1.5">
            {/* ONE ordered child list — subquestions and answers/ideas interleaved by sibling position */}
            {kids.map((c) => (
              <div key={c.node.id}>
                <DropLine target={{ type: "question", id: q.id }} beforeId={c.node.id} />
                {c.kind === "q" ? renderQuestionNode(c.node, depth + 1) : renderIdeaNode(c.node, "answer")}
              </div>
            ))}
            <DropLine target={{ type: "question", id: q.id }} beforeId="__END__" />
          </div>
        )}
      </motion.div>
    );
  };

  // ---- TWS-INFERRED PART node (EARLY EXTERNALIZATION) ----
  // A `node_kind:"group"` item is a PART of the whole TWS externalized from the student's own ideas. SIMPLIFY (UDWW 3C-5):
  // a small heading with a dashed rule while it is TWS's sketch (solid once the student names / adopts it); its members are
  // indented beneath it. Fully workable: drop a card in, rename it (which adopts it), or pull cards out.
  const renderPartNode = (part) => {
    const kids = mergedIdeaChildren(part.id);
    const isDropTgt = dropTarget && dropTarget.type === "idea" && dropTarget.id === part.id;
    const isMoveTgt = mode && mode.kind === "move" && canDrop({ type: mode.srcType, id: mode.srcId }, { type: "idea", id: part.id });
    // a part the STUDENT named / adopted / formed reads as theirs (solid), not as a TWS sketch (dashed)
    const isStudents = part.provenance === "student" || part.provenance === "student_adopted" || part.title_provenance === "student";
    const titleEditing = titleEdit === part.id;
    return (
      <motion.div layout={dragging ? false : "position"} key={part.id} className="relative">
        <motion.div layout={!dragging}
          ref={registerNode(part.id)}
          data-testid={`mm-part-${part.id}`}
          onDragOver={(e) => { if (dragRef.current || drag) { e.preventDefault(); e.stopPropagation(); setDropTarget({ type: "idea", id: part.id }); setDropLine(null); } }}
          onDrop={(e) => { const d = dragRef.current || drag; if (d && canDrop(d, { type: "idea", id: part.id })) { e.preventDefault(); e.stopPropagation(); doMove(d, { type: "idea", id: part.id }, null); } }}
          onClick={(e) => { e.stopPropagation(); if (mode && mode.kind === "move" && isMoveTgt) chooseTarget({ type: "idea", id: part.id }); }}
          className={`group/part relative border-l-2 pl-3 py-1 rounded-r-md transition-colors ${isStudents ? "border-[#8C3A2A]/60" : "border-dashed border-[#8C3A2A]/40"}
            ${(isMoveTgt || isDropTgt) ? "ring-2 ring-emerald-400 border-emerald-400 bg-emerald-50/60" : ""}`}>
          {isMoveTgt && (
            <span data-testid={`mm-place-here-${part.id}`} className="absolute -top-2 -right-2 z-30 inline-flex items-center gap-0.5 rounded-full bg-emerald-500 text-white text-[9px] font-semibold px-1.5 py-0.5 shadow">
              <CornerDownRight className="h-2.5 w-2.5" /> Make it part of this
            </span>
          )}
          {titleEditing ? (
            <span className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
              <input data-testid={`mm-part-title-input-${part.id}`} autoFocus {...otNoPaste} value={titleVal} maxLength={80}
                onChange={(e) => setTitleVal(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") savePartTitle(part); if (e.key === "Escape") setTitleEdit(null); }}
                className="flex-1 rounded border border-[#8C3A2A]/40 px-1.5 py-0.5 text-[13px] font-semibold text-stone-900 outline-none bg-white" />
              <button data-testid={`mm-part-title-save-${part.id}`} onClick={() => savePartTitle(part)} className="text-[11px] font-semibold text-[#8C3A2A]">save</button>
            </span>
          ) : (
            <div className="flex flex-wrap items-baseline gap-x-2 min-w-0">
              <p data-testid={`mm-part-label-${part.id}`} data-owner={isStudents ? "student" : "tws"}
                className={isStudents ? "text-[13px] font-semibold leading-snug text-stone-900" : "text-[13px] font-medium italic leading-snug text-stone-500 border-b border-dashed border-stone-300/80"}>{part.label || "These ideas"}</p>
              {isStudents ? null : <span data-testid={`mm-part-tws-${part.id}`} title="How The Writing Studio reads this part — a starting shape, yours to rename, move or reshape." className="text-[9.5px] italic text-stone-400">how TWS reads this part</span>}
              <button data-testid={`mm-part-rename-${part.id}`} onClick={(e) => { e.stopPropagation(); setTitleVal((part.label || "").trim()); setTitleEdit(part.id); }}
                className="ml-auto inline-flex items-center gap-0.5 text-[9px] text-stone-400 opacity-0 group-hover/part:opacity-100 focus:opacity-100 hover:text-[#8C3A2A] transition-opacity"><Pencil className="h-2.5 w-2.5" /> rename</button>
            </div>
          )}
        </motion.div>
        {kids.length > 0 && (
          <div className="ml-5 mt-1 space-y-1.5">
            {kids.map((c) => (
              <div key={c.node.id}>
                <DropLine target={{ type: "idea", id: part.id }} beforeId={c.node.id} />
                {c.kind === "q" ? renderQuestionNode(c.node, 1) : renderIdeaNode(c.node, "subidea")}
              </div>
            ))}
            <DropLine target={{ type: "idea", id: part.id }} beforeId="__END__" />
          </div>
        )}
      </motion.div>
    );
  };

  // UNIFIED WORKSPACE — ONE piece of developing writing in a broad communicative coordinate system. A root unit is
  // a unit the student (or, provisionally, TWS) has made part of the whole: under the emerging answer, in a region,
  // or a TWS part node. Everything else whose role in the whole is genuinely unresolved rests in THOUGHTS I'M STILL
  // DEVELOPING (the ONE unresolved area — there is no "not placed yet" tray and no second "still organizing" band).
  // ESSAY-AS-WORKSPACE (frame "parts" — the consolidated loop's Structure view, shown once the controller licenses structure): EVERY
  // top-level unit of the student's writing is a part of the essay (in the Body unless the student placed it); nothing is "still
  // developing" and the thesis is not repeated here (it is a marked sentence inside its paragraph in the essay). (`partsFrame` is declared above.)
  // NARROW EXCEPTION (whole differentiation): a top-level part the STUDENT generated in answer to "what else must the essay establish?"
  // (`origin.how === "whole_differentiation"`) that they have not yet placed (no region, no parent) is NOT shown as a Body part — it rests in
  // Thoughts I'm still developing until the student places it; placement itself is the evidence that moves it out.
  const isUnplacedCandidatePart = (i) => (i.origin || {}).how === "whole_differentiation" && !(i.region || "").trim() && !i.parent_id && !i.under_thesis;
  const rootIdeas = looseIdeas.filter((i) => partsFrame ? ((i.node_kind !== "group" || childIdeas(i.id).length > 0) && !isUnplacedCandidatePart(i))
    : (i.under_thesis || (i.region || "").trim() || (i.node_kind === "group" && childIdeas(i.id).length > 0)));
  const developingIdeas = partsFrame ? looseIdeas.filter(isUnplacedCandidatePart)
    : looseIdeas.filter((i) => !i.under_thesis && !(i.region || "").trim() && i.node_kind !== "group");
  const trayIdeas = developingIdeas; // (name kept for the move/drop machinery)
  const mergedRoots = () => [
    ...rootQs.map((n) => ({ kind: "q", node: n })),
    ...rootIdeas.map((n) => ({ kind: "i", node: n })),
  ].sort((a, b) => cmpSib(a.node, b.node));
  const nQuestions = studentQs.length;
  const nIdeas = items.length;

  // NON-DRAG MOVE AFFORDANCE (Boundary round §5): while moving the ACTIVE card, offer a small BOUNDED set of
  // explicit labeled destinations (the student's major thought parts / strands) alongside drag. The interface
  // identifies valid destinations; the STUDENT still makes the consequential structural judgment. Not a big
  // menu of every card — capped, and only destinations the move is actually valid for.
  const _moveSrc = inMove ? { type: mode.srcType, id: mode.srcId } : null;
  const moveDestinations = _moveSrc
    ? mergedRoots()
        .filter((c) => canDrop(_moveSrc, { type: c.kind === "q" ? "question" : "idea", id: c.node.id }))
        .slice(0, 6)
        .map((c) => ({ type: c.kind === "q" ? "question" : "idea", id: c.node.id,
                       label: (shortOf(c.node) || c.node.text || "this part").slice(0, 48) }))
    : [];

  // SIMPLIFY (UDWW 3C-5) — THE EMERGING ESSAY, DIRECTLY. Three plain headings (Introduction / Body / Conclusion) and the
  // emerging thesis; whitespace and indentation carry the hierarchy. No roman numerals, blurbs, boxes or connector lines.
  const REGION_DEFS = [
    { key: "introduction", title: "Introduction" },
    { key: "body", title: "Body" },
    { key: "conclusion", title: "Conclusion" },
  ];
  const DEVELOPING_DEF = { key: "developing", title: "Thoughts I’m still developing" };
  // LOCAL MOVE CONTROL (Structure view): the destinations appear WHERE the action began — beside the moved part — so the student never
  // has to hunt for a highlighted target. Broad regions use the SAME structural-move pathway (chooseTarget → onStructuralMove); "Inside
  // another part…" lists the valid parts here and keeps the highlighted cards selectable — the STUDENT makes the relation, nothing is
  // chosen or scrolled to for them. Escape / Cancel leave the structure untouched.
  const localMoveBar = (i) => {
    const curRegion = (i.region || "").trim().toLowerCase();
    const cancelBtn = <button data-testid="mm-mode-cancel" onClick={() => { setMode(null); setMoveLevel("top"); }} className="inline-flex items-center gap-1 px-1 text-emerald-700 hover:text-emerald-900"><X className="h-3.5 w-3.5" /> Cancel</button>;
    return (
      <div data-testid={`mm-local-move-${i.id}`} data-level={moveLevel} onClick={(e) => e.stopPropagation()}
        className="mt-1.5 w-full max-w-full min-w-0 rounded-md bg-emerald-50 border border-emerald-300 px-3 py-2 text-[12.5px] text-emerald-900">
        {moveLevel === "top" ? (
          <div className="flex flex-col gap-1.5" data-testid="mm-move-level-top">
            <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-emerald-700">Move to:</span>
            <div className="flex flex-wrap items-center gap-2">
              {REGION_DEFS.map((r) => (
                <button key={r.key} data-testid={`mm-move-region-${r.key}`} disabled={curRegion === r.key && !i.parent_id} aria-current={curRegion === r.key && !i.parent_id ? "true" : undefined}
                  onClick={() => chooseTarget({ type: "region", region: r.key })}
                  className="rounded-full border border-[#8C3A2A]/50 bg-white text-[#8C3A2A] px-3 py-1 hover:bg-[#8C3A2A]/10 disabled:opacity-40 disabled:cursor-default">{r.title}</button>
              ))}
              {moveDestinations.length > 0 && (
                <button data-testid="mm-move-another" onClick={() => setMoveLevel("ideas")} className="rounded-full border border-emerald-400 bg-white px-3 py-1 hover:bg-emerald-100">Inside another part&hellip;</button>
              )}
              {cancelBtn}
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-1.5" data-testid="mm-move-level-ideas">
            <span data-testid="mm-move-inside-hint" className="text-[12px] text-emerald-800">Choose the part this belongs inside.</span>
            <div className="flex flex-wrap items-center gap-2">
              {moveDestinations.map((d) => (
                <button key={d.id} data-testid={`mm-move-under-${d.id}`} onClick={() => chooseTarget({ type: d.type, id: d.id })}
                  className="rounded-full border border-emerald-400 bg-white px-3 py-1 hover:bg-emerald-100 max-w-full text-left">{d.label}</button>
              ))}
              <button data-testid="mm-move-back" onClick={() => setMoveLevel("top")} className="inline-flex items-center gap-1 px-1 text-emerald-700 hover:text-emerald-900">&larr; Back</button>
              {cancelBtn}
            </div>
          </div>
        )}
      </div>
    );
  };
  // WRITE HERE — a paragraph typed (or their own words pasted) straight into a broad region: the plain target for moving prose between
  // Introduction / Body / Conclusion. A mechanical affordance on the existing add + place calls; no drag/drop, no structural authority.
  const rwKey = (key) => `new:${key}`;
  const openRegionWrite = (key) => { const d = getDraft(draftScope, rwKey(key)); setRegionWrite({ key, text: d ? d.text : "" }); };
  const changeRegionWrite = (v) => { setDraft(draftScope, rwKey(regionWrite.key), v, ""); setRegionWrite({ ...regionWrite, text: v }); };
  const cancelRegionWrite = () => { if (regionWrite) clearDraft(draftScope, rwKey(regionWrite.key)); setRegionWrite(null); };
  const saveRegionWrite = async () => {
    if (!regionWrite) return;
    const { key } = regionWrite;
    const t = regionWrite.text.trim();
    if (!t) { cancelRegionWrite(); return; }
    clearDraft(draftScope, rwKey(key)); setRegionWrite(null);
    try { await onCreateIdea({ type: "region", region: key }, t); bump(); } catch { setDraft(draftScope, rwKey(key), t, ""); setRegionWrite({ key, text: t }); }
  };
  useEffect(() => {
    if (regionWrite) return;
    for (const def of REGION_DEFS) { const d = getDraft(draftScope, rwKey(def.key)); if (draftChanged(d)) { setRegionWrite({ key: def.key, text: d.text }); return; } }
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps
  const regionWriteEl = (def) => (regionWrite && regionWrite.key === def.key ? (
    <div data-testid={`mm-region-write-${def.key}`} className="mt-2" onClick={(e) => e.stopPropagation()}>
      <textarea autoFocus data-testid={`mm-region-write-input-${def.key}`} {...otNoPaste} value={regionWrite.text} rows={3}
        ref={(el) => autoGrow(el, EDIT_MIN_PX)}
        onChange={(e) => { changeRegionWrite(e.target.value); autoGrow(e.target, EDIT_MIN_PX); }}
        onKeyDown={entryKeys(saveRegionWrite, cancelRegionWrite)}
        placeholder={`A paragraph for the ${def.title.toLowerCase()}, in your words — or paste words you cut from another part…`}
        className="block w-full min-w-0 resize-none min-h-[96px] max-h-[60vh] text-[13.5px] leading-relaxed text-stone-800 placeholder:text-stone-400 outline-none border border-[#8C3A2A]/40 focus:border-[#8C3A2A] rounded-md px-3 py-2.5 bg-white" />
      <div className="flex items-center justify-end gap-2 mt-1">
        <span className="mr-auto text-[10px] text-stone-400">Return saves · Shift+Return for a new line</span>
        <button data-testid={`mm-region-write-cancel-${def.key}`} onClick={cancelRegionWrite} className="text-[11px] text-stone-500 px-2">Cancel</button>
        <button data-testid={`mm-region-write-save-${def.key}`} onClick={saveRegionWrite} disabled={!regionWrite.text.trim()}
          className="inline-flex items-center gap-1 bg-[#8C3A2A] text-white text-[11px] px-3 py-1 rounded disabled:opacity-40"><Check className="h-3 w-3" /> Save</button>
      </div>
    </div>
  ) : (
    <button data-testid={`mm-region-write-open-${def.key}`} onClick={(e) => { e.stopPropagation(); openRegionWrite(def.key); }}
      className="mt-1.5 inline-flex items-center gap-1 text-[11px] text-stone-400 hover:text-[#8C3A2A]"><Plus className="h-3 w-3" /> Write here</button>
  ));
  const REGION_KEYS = REGION_DEFS.map((r) => r.key);
  const regionOfNode = (n) => (n.region || "").trim().toLowerCase();
  const regionBands = () => {
    const groups = { introduction: [], body: [], conclusion: [] };
    mergedRoots().forEach((c) => {
      const rg = regionOfNode(c.node);
      if (REGION_KEYS.includes(rg)) groups[rg].push(c);
      else groups.body.push({ ...c, provisionalLocation: true });
    });
    return groups;
  };

  const renderStrandChild = (c, role, num) => (
    <div key={c.node.id} className="relative">
      <DropLine target={{ type: "root" }} beforeId={c.node.id} />
      <div className="flex items-start gap-2">
        {num ? <span data-testid={`mm-num-${c.node.id}`} className="mt-1.5 w-4 shrink-0 text-right font-mono text-[10px] text-stone-400 select-none">{num}</span> : null}
        <div className="min-w-0 flex-1">
          {c.kind === "q" ? renderQuestionNode(c.node, 0) : (c.node.node_kind === "group" ? renderPartNode(c.node) : renderIdeaNode(c.node, role || "root"))}
        </div>
      </div>
    </div>
  );

  // EMERGING THESIS — the student's own words (or nothing yet). While TWS reads an emerging answer in the student's words
  // (a pending candidate, or its persistent provisional reading), it shows here as EMERGING (dashed) — the structure shows what
  // the essay is becoming. ROUTING: tapping it opens the thesis editor pre-filled with those words: keep as is = adopt; change = revise.
  const thesisBand = ((thesisRegion || "introduction").toLowerCase());
  const emergingCandidate = !thesis?.trim()
    ? ((thesisProposal && (thesisProposal.text || "").trim()) || (provisionalThesis && (provisionalThesis.text || "").trim()) || "")
    : "";
  const openThesisEdit = () => { if (!thesis?.trim() && emergingCandidate && !thesisDraft.trim()) setThesisDraft(emergingCandidate); setThesisEdit(true); };
  const renderThesisUnit = () => (
    <div data-testid="mm-thesis-in-region" onClick={(e) => { e.stopPropagation(); if (mode && mode.kind === "move") { chooseTarget({ type: "root" }); } else if (!mode) openThesisEdit(); }}
      onDragOver={(e) => { if (dragRef.current || drag) { e.preventDefault(); e.stopPropagation(); setDropTarget({ type: "root" }); setDropLine(null); } }}
      onDrop={(e) => { const d = dragRef.current || drag; if (d) { e.preventDefault(); e.stopPropagation(); doMove(d, { type: "root" }, null); } }}
      className={`relative mb-3 pl-3 py-1 cursor-pointer transition-colors border-l-2 ${thesis?.trim() ? "border-[#8C3A2A]" : "border-dashed border-[#8C3A2A]/40"}
        ${((mode && mode.kind === "move") || dropTarget?.type === "root") ? "ring-2 ring-emerald-400 rounded bg-emerald-50/60" : ""}`}>
      {mode && mode.kind === "move" && (
        <span data-testid="mm-place-here-thesis" className="absolute -top-2 -right-2 z-30 inline-flex items-center gap-0.5 rounded-full bg-emerald-500 text-white text-[9px] font-semibold px-1.5 py-0.5 shadow">
          <CornerDownRight className="h-2.5 w-2.5" /> Make it part of my answer
        </span>
      )}
      <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-[#8C3A2A] mb-0.5">Emerging thesis <span className="text-stone-400 normal-case tracking-normal">· yours to change</span></p>
      {thesisEdit ? (
        <div onClick={(e) => e.stopPropagation()}>
          <textarea autoFocus data-testid="mm-thesis-input" {...otNoPaste} value={thesisDraft} rows={2}
            onChange={(e) => setThesisDraft(e.target.value)} onBlur={() => { onSaveThesis(thesisDraft); setThesisEdit(false); }}
            placeholder="Blank is fine for now — write it when an answer starts to form, or let it grow out of your parts…"
            className="w-full resize-none font-serif text-[15px] text-stone-900 placeholder:text-stone-400 border border-[#8C3A2A]/40 rounded p-2 outline-none bg-white" />
        </div>
      ) : emergingCandidate ? (
        <>
          <p data-testid="mm-thesis-emerging" className="font-serif text-[15px] leading-snug text-stone-800 italic">“{emergingCandidate}”</p>
          <p className="mt-0.5 text-[10.5px] text-stone-400">{thesisProposal && (thesisProposal.text || "").trim() ? "in your words · provisional · decide it in the conversation" : "in your words · how The Writing Studio reads your answer so far · tap to keep or change it"}</p>
        </>
      ) : (
        <p data-testid="mm-thesis-text" className={`font-serif text-[15px] leading-snug ${thesis?.trim() ? "text-stone-900" : "text-stone-400 italic"}`}>{thesis?.trim() || "Not sure yet — tap to start it, or let it form from your parts"}</p>
      )}
    </div>
  );

  const renderRegionBand = (def, roots, withThesis = true) => {
    const isDrop = dropTarget?.type === "region" && dropTarget?.region === def.key;
    let n = 0;
    return (
      <section data-testid={`mm-region-${def.key}`}
        onDragOver={(e) => { if (dragRef.current || drag) { e.preventDefault(); e.stopPropagation(); setDropTarget({ type: "region", region: def.key }); setDropLine(null); } }}
        onDrop={(e) => { const d = dragRef.current || drag; if (d && canDrop(d, { type: "region", region: def.key })) { e.preventDefault(); e.stopPropagation(); doMove(d, { type: "region", region: def.key }, null); } }}
        className={`rounded-md px-1 py-1 transition-colors ${isDrop ? "ring-2 ring-emerald-400 bg-emerald-50/60" : ""}`}>
        <h4 className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-400 border-b border-stone-200/80 pb-1 mb-2.5">{def.title}</h4>
        {withThesis && def.key === thesisBand ? renderThesisUnit() : null}
        {roots.length > 0 ? (
          <div className="space-y-2">{roots.map((c) => renderStrandChild(c, "root", def.key === "body" && c.kind !== "q" ? ++n : 0))}</div>
        ) : (drag ? (
          <p className="text-[11px] text-emerald-700/70 italic px-1">drop here</p>
        ) : (def.key === thesisBand ? null : <div className="h-2" />))}
        {partsFrame && onCreateIdea ? regionWriteEl(def) : null}
      </section>
    );
  };

  // THOUGHTS I'M STILL DEVELOPING — only for material whose place genuinely cannot yet be inferred (also the "keep separate" drop target).
  // Quiet: the heading appears only when something is here; the way to write a loose thought is always one small link.
  const renderDevelopingBand = () => {
    const hasAny = unplacedQs.length > 0 || trayIdeas.length > 0;
    return (
      <section data-testid="mm-region-developing"
        onDragOver={(e) => { if (dragRef.current || drag) { e.preventDefault(); e.stopPropagation(); setDropTarget({ type: "loose" }); setDropLine(null); } }}
        onDrop={(e) => { const d = dragRef.current || drag; if (d) { e.preventDefault(); e.stopPropagation(); doMove(d, { type: "loose" }, null); } }}
        className={`rounded-md px-1 py-1 transition-colors ${dropTarget?.type === "loose" ? "ring-2 ring-emerald-400 bg-emerald-50/60" : ""}`}>
        {hasAny || addOpen || drag ? (
          <h4 className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-400 border-b border-dashed border-stone-200/80 pb-1 mb-2.5">
            {frame === "writing" ? "My writing" : DEVELOPING_DEF.title}{drag ? <span className="normal-case tracking-normal text-emerald-700/70"> · drop here to keep it separate</span> : null}
          </h4>
        ) : null}
        {hasAny ? (
          <LayoutGroup><div className="space-y-2">
            {unplacedQs.map((q) => renderQuestionNode(q, 0))}
            {trayIdeas.map((i) => renderIdeaNode(i, "loose"))}
          </div></LayoutGroup>
        ) : null}
        {addOpen ? (
          <div className="mt-2">
            <textarea data-testid="mm-add-idea-input" autoFocus {...otNoPaste} value={addText} onChange={(e) => setAddText(e.target.value)} rows={2}
              onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { (addKind === "question" ? onCreateQuestion({ type: "loose" }, addText) : onAddLoose(addText)); setAddText(""); setAddOpen(false); } }}
              placeholder={addKind === "question" ? "Something you need to figure out — it waits here until you place it." : "A thought whose place in your essay isn’t clear yet — write it here; move it when it finds its place."}
              className="w-full resize-none text-[13px] text-stone-800 placeholder:text-stone-400 border border-stone-300 rounded-md p-2 outline-none focus:border-[#8C3A2A] bg-white" />
            <div className="flex justify-end gap-2 mt-1">
              <button onClick={() => { setAddOpen(false); setAddText(""); }} className="text-[11px] text-stone-500 px-2">Cancel</button>
              <button data-testid="mm-add-idea-btn" onClick={() => { (addKind === "question" ? onCreateQuestion({ type: "loose" }, addText) : onAddLoose(addText)); setAddText(""); setAddOpen(false); }} disabled={!addText.trim()} className="inline-flex items-center gap-1 bg-[#8C3A2A] text-white text-[11px] px-3 py-1 rounded disabled:opacity-40"><Plus className="h-3 w-3" /> Add</button>
            </div>
          </div>
        ) : (
          <div className={`flex items-center gap-3 ${hasAny ? "mt-2" : ""}`}>
            {SHOW_GENERIC_ADD_QUESTION && <button data-testid="mm-add-question-loose" onClick={() => { setAddKind("question"); setAddOpen(true); }} className="inline-flex items-center gap-1 text-[11px] text-[#8C3A2A] hover:text-[#6B2C20]"><Plus className="h-3.5 w-3.5" /> Question</button>}
            <button data-testid="mm-add-idea-open" onClick={() => { setAddKind("idea"); setAddOpen(true); }} className="inline-flex items-center gap-1 text-[11.5px] text-stone-500 hover:text-[#8C3A2A]"><Plus className="h-3.5 w-3.5" /> Write a thought</button>
          </div>
        )}
      </section>
    );
  };

  return (
    <div data-testid="thinking-map" data-tour="structure-map" className="space-y-3" {...otOwnCopy}>
      {/* top bar: undo only (structural controls stay quiet) */}
      {canUndo && (
        <div className="flex items-center justify-end px-1">
          <button data-testid="mm-undo" onClick={() => { onUndo(); bump(); }}
            className="inline-flex items-center gap-1 text-[11px] font-medium text-[#8C3A2A] border border-[#8C3A2A]/30 rounded-full px-2.5 py-1 hover:bg-[#8C3A2A]/5">
            <Undo2 className="h-3.5 w-3.5" /> Undo last move
          </button>
        </div>
      )}
      {/* SIMPLIFY (UDWW 3C-5): no orientation narration — the workspace itself answers "what is my essay becoming?" */}

      {/* STRUCTURAL-MOVE CONTROL — lives INSIDE the left structural workspace (static block above the canvas; never
          sticky/overlaying the map or the dialogue; wraps within its column). Progressive disclosure: level 1 names
          the structural decision (MAKE THIS PART OF: main idea / regions / another idea… / keep separate / cancel);
          "Another idea…" THEN reveals the eligible card destinations. Same operations, same semantics as before. */}
      {mode && (
        <div data-testid="mm-mode-banner" data-level={mode.kind === "move" ? moveLevel : "connect"}
          className="w-full max-w-full min-w-0 flex flex-col gap-2 rounded-md bg-emerald-50 border border-emerald-300 px-3 py-2.5 text-[12.5px] text-emerald-900">
          <div className="flex items-start gap-1.5 min-w-0">
            <Target className="h-3.5 w-3.5 shrink-0 mt-0.5" />
            {mode.kind === "move" ? (
              <span className="min-w-0 break-words">Moving <b className="font-semibold">&ldquo;{movingText.slice(0, 60)}{movingText.length > 60 ? "…" : ""}&rdquo;</b> &mdash; {partsFrame ? "choose where it goes, right beside it (Esc cancels)." : "tap a highlighted card, or choose below."}</span>
            ) : mode.kind === "join" ? (
              <span className="min-w-0 break-words">Joining <b className="font-semibold">&ldquo;{movingText.slice(0, 60)}{movingText.length > 60 ? "…" : ""}&rdquo;</b> &mdash; tap the unit that is really the same point. Its words follow, unchanged.</span>
            ) : "Click the idea this connects to."}
          </div>
          {mode.kind === "move" && moveLevel === "top" && !partsFrame && (
            <div className="flex flex-col gap-1.5" data-testid="mm-move-level-top">
              <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-emerald-700">Make this part of:</span>
              <div className="flex flex-wrap items-center gap-2">
                <button data-testid="mm-move-root" onClick={() => chooseTarget({ type: "root" })} className="rounded-full border border-emerald-400 bg-white px-3 py-1 hover:bg-emerald-100">My answer</button>
                {REGION_DEFS.map((r) => (
                  <button key={r.key} data-testid={`mm-move-region-${r.key}`} onClick={() => chooseTarget({ type: "region", region: r.key })}
                    className="rounded-full border border-[#8C3A2A]/50 bg-white text-[#8C3A2A] px-3 py-1 hover:bg-[#8C3A2A]/10">{r.title}</button>
                ))}
                {moveDestinations.length > 0 && (
                  <button data-testid="mm-move-another" onClick={() => setMoveLevel("ideas")} className="rounded-full border border-emerald-400 bg-white px-3 py-1 hover:bg-emerald-100">Another idea&hellip;</button>
                )}
                <button data-testid="mm-move-loose" onClick={() => chooseTarget({ type: "loose" })} className="rounded-full border border-emerald-400 bg-white px-3 py-1 hover:bg-emerald-100">Still developing</button>
                <button data-testid="mm-mode-cancel" onClick={() => { setMode(null); setMoveLevel("top"); }} className="inline-flex items-center gap-1 px-1 text-emerald-700 hover:text-emerald-900"><X className="h-3.5 w-3.5" /> Cancel</button>
              </div>
            </div>
          )}
          {mode.kind === "move" && moveLevel === "ideas" && !partsFrame && (
            <div className="flex flex-col gap-1.5" data-testid="mm-move-level-ideas">
              <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-emerald-700">Make it part of which idea?</span>
              <div className="flex flex-wrap items-center gap-2">
                {moveDestinations.map((d) => (
                  <button key={d.id} data-testid={`mm-move-under-${d.id}`} onClick={() => chooseTarget({ type: d.type, id: d.id })}
                    className="rounded-full border border-emerald-400 bg-white px-3 py-1 hover:bg-emerald-100 max-w-full text-left">{d.label}</button>
                ))}
                <button data-testid="mm-move-back" onClick={() => setMoveLevel("top")} className="inline-flex items-center gap-1 px-1 text-emerald-700 hover:text-emerald-900">&larr; Back</button>
                <button data-testid="mm-mode-cancel" onClick={() => { setMode(null); setMoveLevel("top"); }} className="inline-flex items-center gap-1 px-1 text-emerald-700 hover:text-emerald-900"><X className="h-3.5 w-3.5" /> Cancel</button>
              </div>
            </div>
          )}
          {(mode.kind === "connect" || mode.kind === "join") && (
            <div className="flex flex-wrap items-center gap-2">
              <button data-testid="mm-mode-cancel" onClick={() => setMode(null)} className="inline-flex items-center gap-1 px-1 text-emerald-700 hover:text-emerald-900"><X className="h-3.5 w-3.5" /> Cancel</button>
            </div>
          )}
        </div>
      )}

      <div ref={mapRef} data-testid="mm-canvas"
        className={`relative overflow-auto ${spacious ? "px-4 py-5 sm:px-8" : "px-2 py-3 sm:px-4"}`}
        style={{ minHeight: spacious ? 480 : 320 }}
        onClick={() => { if (!mode) clearInteractions(); }}>

        {/* CORRECTION #2 (authentic test): the long dotted cross-map relationship arrows and their floating
            "contrasts with" chips are HIDDEN from the student-facing map — they added visual load without making the
            relation usable. Relationship DATA, endpoints, validation (placedNodeIdSet) and the edge geometry are all
            preserved; a simpler LOCAL notation will be tested later. Flip SHOW_CROSS_EDGES to restore. */}
        {SHOW_CROSS_EDGES ? (<>
        {/* SVG cross-branch connectors (behind nodes) */}
        <svg data-testid="mm-edges" className="absolute inset-0 pointer-events-none" width={dims.w} height={dims.h} style={{ zIndex: 1 }}>
          <defs>
            <marker id="mm-arrow" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto" markerUnits="strokeWidth">
              <path d="M0,0 L6,3 L0,6 Z" fill={TERRA} opacity="0.7" />
            </marker>
          </defs>
          {edges.map((e) => {
            const mx = (e.x1 + e.x2) / 2, my = (e.y1 + e.y2) / 2 - 24;
            // STUDENT-OWNED relation: solid, ordinary; TWS-PROPOSED: lighter and dashed — the same distinction the chip carries in words
            return <path key={e.id} data-testid={`mm-edge-${e.id}`} data-owner={e.tws ? "tws" : "student"} d={`M ${e.x1} ${e.y1} Q ${mx} ${my} ${e.x2} ${e.y2}`} fill="none" stroke={TERRA}
              strokeOpacity={e.tws ? "0.3" : "0.6"} strokeWidth="1.5" strokeDasharray={e.tws ? "4 3" : undefined} markerEnd="url(#mm-arrow)" />;
          })}
        </svg>
        {/* REPAIR §7: compact relationship chip. The wrapper is pointer-events-none so it NEVER intercepts a
            card's select/edit/move; only the small chip is clickable, and it reveals the full student relation. */}
        {edges.map((e) => e.label ? (
          <div key={`lbl-${e.id}`} className="absolute z-20 -translate-x-1/2 -translate-y-1/2 pointer-events-none"
               style={{ left: (e.x1 + e.x2) / 2, top: (e.y1 + e.y2) / 2 - 10 }}>
            <button data-testid={`mm-rel-${e.id}`} data-owner={e.tws ? "tws" : "student"} onClick={(ev) => { ev.stopPropagation(); setRelView({ id: e.id, text: e.label, tws: e.tws }); }}
              title={e.tws ? `How The Writing Studio reads this connection — tap to keep it, or remove it: ${e.label}` : e.label}
              className={`pointer-events-auto inline-flex items-center gap-1 rounded-full bg-white px-2 py-0.5 text-[10px] shadow-sm max-w-[170px] ${e.tws
                ? "border border-dashed border-stone-300 italic text-stone-400 hover:border-[#8C3A2A]/60"
                : "border border-[#8C3A2A]/40 text-[#8C3A2A] hover:border-[#8C3A2A]"}`}>
              <span className="truncate">{e.tws ? `TWS sees: ${compactRel(e.label)}` : compactRel(e.label)}</span>
            </button>
          </div>
        ) : null)}
        {/* REPAIR §7: tap-to-reveal the full relationship (touch-friendly), with remove here rather than a
            hover-only X that could sit over a card. */}
        {relView ? (
          <div className="fixed inset-0 z-50 grid place-items-center bg-stone-900/30 px-4" onClick={() => setRelView(null)}>
            <div data-testid="mm-rel-view" onClick={(ev) => ev.stopPropagation()} className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl">
              <p data-testid="mm-rel-view-heading" className={relView.tws ? "text-[11px] italic text-stone-400 mb-1" : "font-mono text-[9px] uppercase tracking-[0.14em] text-stone-400 mb-1"}>
                {relView.tws ? "How The Writing Studio reads this connection — yours to keep or remove" : "How you connected these"}</p>
              <p className={`text-[14px] leading-relaxed ${relView.tws ? "text-stone-600 italic" : "text-stone-800"}`}>{relView.text}</p>
              <div className="flex justify-between items-center mt-4 gap-3">
                <button data-testid="mm-rel-remove" onClick={() => { onDeleteRel(relView.id); setRelView(null); bump(); }}
                  className="inline-flex items-center gap-1.5 text-[12.5px] text-rose-600 hover:text-rose-700"><X className="h-3.5 w-3.5" /> {relView.tws ? "Not this" : "Remove connection"}</button>
                {relView.tws && onAdoptProvisionalRel ? (
                  <button data-testid="mm-rel-keep" onClick={() => { onAdoptProvisionalRel(relView.id); setRelView(null); bump(); }}
                    className="text-[13px] px-3 py-1.5 rounded-md bg-[#8C3A2A] text-white hover:bg-[#6b2a1e]">Keep</button>
                ) : null}
                <button onClick={() => setRelView(null)} className="text-[13px] px-3 py-1.5 rounded-md bg-stone-100 text-stone-600 hover:bg-stone-200">Close</button>
              </div>
            </div>
          </div>
        ) : null}
        </>) : null}

        <LayoutGroup>
        <div className="relative z-10 space-y-3">
          {/* top-level drop zone (visible only while dragging) */}
          {drag && (
            <div data-testid="mm-root-dropzone"
              onDragOver={(e) => { e.preventDefault(); setDropTarget({ type: "root" }); setDropLine(null); }}
              onDrop={(e) => { e.preventDefault(); doMove(dragRef.current || drag, { type: "root" }, null); }}
              className={`rounded-lg border-2 border-dashed px-3 py-2 text-[11px] text-center transition-colors ${dropTarget?.type === "root" ? "border-emerald-400 bg-emerald-50 text-emerald-700" : "border-stone-300 text-stone-400"}`}>
              ↥ Drop here to make it part of your answer
            </div>
          )}

          {/* ONE piece of developing writing — the emerging essay, directly (whitespace between its parts) */}
          <div data-testid="mm-regions" className="space-y-6" data-frame={frame}>
            {partsFrame && thesis?.trim() ? (
              // PROVISIONAL THESIS (consolidated Structure view): the student-owned working thesis — the SAME authority as the underlined
              // sentence in the essay (their stated / accepted wording; never TWS's private reading). Same label, same underline, same thesis.
              <div data-testid="mm-provisional-thesis" data-owner={thesisRecognized ? "tws" : "student"} className={`pl-3 py-1 border-l-2 ${thesisRecognized ? "border-dashed border-[#8C3A2A]/30" : "border-[#8C3A2A]/40"}`}>
                <p data-testid="mm-provisional-thesis-label" className={thesisRecognized ? "text-[10.5px] italic text-stone-400 mb-0.5" : "font-mono text-[9px] uppercase tracking-[0.18em] text-[#8C3A2A] mb-0.5"}>
                  {thesisRecognized ? "TWS reads this as your main idea" : "Provisional thesis"}</p>
                <p data-testid="mm-provisional-thesis-text" className="font-serif text-[15px] leading-snug text-stone-900">
                  <span className={thesisRecognized ? "underline decoration-dashed decoration-[#8C3A2A]/45 underline-offset-[5px]" : "underline decoration-[#8C3A2A]/60 decoration-2 underline-offset-[5px]"}>{thesis.trim()}</span>
                </p>
              </div>
            ) : null}
            {(() => {
              const g = regionBands();
              if (frame === "writing") {
                // WRITING FRAME: the emerging thesis, then the student's writing. An essay region shows only once the STUDENT placed something in it.
                return (<>
                  <div data-testid="mm-writing-frame">{renderThesisUnit()}</div>
                  {REGION_DEFS.filter((def) => (g[def.key] || []).length > 0).map((def) => <div key={def.key}>{renderRegionBand(def, g[def.key], false)}</div>)}
                </>);
              }
              return REGION_DEFS.map((def) => <div key={def.key}>{renderRegionBand(def, g[def.key], !partsFrame)}</div>);
            })()}
            {renderDevelopingBand()}
          </div>

          {nQuestions === 0 && nIdeas === 0 && !hasThesis && (
            <p data-testid="mm-empty" className="text-[12px] text-stone-400 italic py-2">Your essay will take shape here as you talk and write.</p>
          )}
        </div>
        </LayoutGroup>
      </div>

      {/* connect label dialog */}
      {connectLabel && (
        <div className="fixed inset-0 z-40 grid place-items-center bg-stone-900/30 px-4" onClick={() => setConnectLabel(null)}>
          <div data-testid="mm-connect-dialog" onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl">
            <p className="font-serif text-lg text-stone-900 mb-1">How are these connected?</p>
            <p className="text-[13px] text-stone-600 mb-3"><span className="font-medium">{shortOf(itemById(connectLabel.fromId))}</span> → <span className="font-medium">{shortOf(itemById(connectLabel.toId))}</span></p>
            <input autoFocus data-testid="mm-connect-input" {...otNoPaste} value={connectLabel.text} onChange={(e) => setConnectLabel({ ...connectLabel, text: e.target.value })}
              onKeyDown={(e) => { if (e.key === "Enter") commitConnect(); }} placeholder="in your own words — because, leads to, contrasts with, is an example of…"
              className="w-full text-[14px] px-3 py-2 border border-stone-300 rounded-md outline-none focus:border-[#8C3A2A]" />
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setConnectLabel(null)} className="text-[13px] px-3 py-1.5 text-stone-500">Cancel</button>
              <button data-testid="mm-connect-save" onClick={commitConnect} className="inline-flex items-center gap-1.5 bg-[#8C3A2A] text-white text-[13px] px-4 py-1.5 rounded-md hover:bg-[#6B2C20]"><Link2 className="h-3.5 w-3.5" /> Connect</button>
            </div>
          </div>
        </div>
      )}

      {/* delete-with-children choice */}
      {confirmDel && (
        <div className="fixed inset-0 z-40 grid place-items-center bg-stone-900/30 px-4" onClick={() => setConfirmDel(null)}>
          <div data-testid="mm-delete-dialog" onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl">
            <p className="font-serif text-lg text-stone-900 mb-1">This box has things underneath it</p>
            <p className="text-[13px] text-stone-600 mb-4">What should happen to what's beneath it?</p>
            <div className="space-y-2">
              <button data-testid="mm-delete-promote" onClick={() => { onDelete(confirmDel, "promote"); setConfirmDel(null); setSelected(null); }}
                className="w-full text-left rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2.5 hover:bg-emerald-100">
                <span className="block text-[13px] font-medium text-emerald-800">Keep them — move them up one level</span>
                <span className="block text-[11px] text-emerald-700">Safer. Only this box is removed; its contents stay.</span>
              </button>
              <button data-testid="mm-delete-branch" onClick={() => { onDelete(confirmDel, "branch"); setConfirmDel(null); setSelected(null); }}
                className="w-full text-left rounded-md border border-rose-200 px-3 py-2.5 hover:bg-rose-50">
                <span className="block text-[13px] font-medium text-rose-700">Delete this whole branch</span>
                <span className="block text-[11px] text-rose-500">Removes this box and everything beneath it.</span>
              </button>
            </div>
            <div className="flex justify-end mt-4"><button onClick={() => setConfirmDel(null)} className="text-[13px] px-3 py-1.5 text-stone-500">Cancel</button></div>
          </div>
        </div>
      )}
    </div>
  );
}
